class CustomKit extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId || 'default';
    this.pageHandle = this.dataset.pageHandle || this.sectionId;
    this.SS_KEY = `custom-kit:pending-selection:${this.pageHandle}`;
    this.collections = [];
    this.selectedItems = []; // Array of product objects for the custom kit
    this.customKitProduct = null; // The custom kit product data
    this.strings = {}; // Text strings for the custom kit
    this.currentCollectionIndex = 0; // Track which collection is currently visible
    this.currentHeaderTileIndex = 0; // Track the current header tile index (global, 0-based)
    this.pendingScrollTarget = null; // Product to scroll to after navigation
    this.lastActionTileIndex = null; // Tile index associated with the last user action (e.g., removal)
    this.totalAmountSaved = 0; // Total savings across all selected items
    this.collectionAvailability = []; // Availability data per collection
    this.headerSwiper = null; // Swiper instance for header tiles
    this.headerTileCount = 0; // Total number of header tiles
    this.headerDom = null; // Cached header DOM references for scroll work
    this.pendingAnimation = null; // Pending grid animation data
    this.animationTimeoutId = null; // Timeout ID for animation cleanup
    this.editMode = false; // Whether we're editing an existing kit
    this.editLineKey = null; // The line item key being edited
    this.wasAddToBagVisible = false; // Track CTA visibility to animate hide state
    this.quickshopStrings = {}; // Quickshop-specific text strings
    this.defaultQuickshopCTAMarkup = null; // Cache for default CTA markup
    this.variantChangeHandler = null; // Quickshop variant change handler
    this.joinWaitlistListener = null; // Quickshop waitlist listener
    this.shadeListDoneListener = null; // Shade list modal done listener
    this.allRequirementsMet = false; // Customer has full selection
    this.itemWasUpdated = false; // Customer updated an item at least once
    this.removedOosTileIndices = []; // Track tile indices of auto-removed OOS items (for edit mode)
    this.pendingOosEditLiveCheck = false; // Whether we should refetch live availability after reload
    this.cartVariantQuantities = new Map(); // Variant quantities already in cart, including bundle components
  }

  connectedCallback() {
    this.init();
  }

  formatSavingsCents(cents) {
    if (typeof window.TarteCurrencyFormatter?.formatCents !== 'function') {
      return `$${(Number(cents) / 100).toFixed(2)}`;
    }

    return window.TarteCurrencyFormatter.formatCents(cents, {
      trimTrailingZeros: true,
    });
  }

  async init() {
    // Check if we're in edit mode (ck_line_key in URL)
    this.checkEditMode();

    // Load settings
    this.loadSettings();

    // Load text strings
    this.loadStrings();
    this.loadQuickshopStrings();

    // Get custom kit product data
    this.loadCustomKitProduct();

    // Get all collection data from script tags
    this.loadCollections();

    // Load cart usage before calculating inventory-sensitive availability
    await this.refreshCartVariantQuantities({ forceFetch: true });

    // Calculate availability data from loaded collections
    this.calculateCollectionAvailability();

    // exit early if a collection is sold out
    if (this.applySoldOutState()) return;

    // Restore selected items from URL params
    this.loadFromURLParams();
    this.refreshSelectedItemsAvailability();

    // On OOS edit reload, ensure we refresh availability before proceeding
    if (this.pendingOosEditLiveCheck) {
      this.refreshSelectedItemsAvailability();
      try {
        await this.fetchLiveAvailability();
      } catch (error) {
        console.error('Custom Kit: Error fetching live availability on OOS edit reload:', error);
        this.refreshSelectedItemsAvailability();
      }
      this.cleanupOosTileTracking();
      this.pendingOosEditLiveCheck = false;
    }

    // Set the active collection and header tile index based on what's already selected
    this.updateCurrentIndicesToNextEmpty();

    // If editing a kit with OOS items, navigate to the first OOS item
    this.navigateToFirstOosItemIfEditing();

    // Calculate total savings (in case items were loaded from URL)
    this.calculateTotalAmountSaved();

    // Update the allRequirementsMet flag
    this.updateAllRequirementsMet();

    // Bind event listeners once using event delegation
    this.bindEventListeners();

    // Initialize the custom kit UI
    this.render();

    // Attach shade list done handler for quickshop flows
    this.attachShadeListDoneHandler();

    // Check if max custom kits in cart reached (non-blocking)
    this.isCustomKitMaxReached().then(isMaxReached => {
      this.maxKitsReached = isMaxReached;
    });

    // Initialize scroll handler for minified header (desktop only)
    this.initScrollHandler();
  }

  loadSettings() {
    const settingsScript = this.querySelector('script.custom-kit-settings');

    if (settingsScript) {
      try {
        this.settings = JSON.parse(settingsScript.textContent);
        console.log('Loaded custom kit settings:', this.settings);
      } catch (error) {
        console.error('Error parsing settings data:', error);
        this.settings = {};
      }
    } else {
      this.settings = {};
    }
  }

  /**
   * Check if we're in edit mode based on URL params
   * Sets editMode and editLineKey if ck_line_key is present and exists in cart data
   */
  checkEditMode() {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('oos_edit')) {
      this.editMode = true;
      this.pendingOosEditLiveCheck = true;
      const url = new URL(window.location.href);
      url.searchParams.delete('oos_edit');
      window.history.replaceState({}, '', url.toString());
    }

    const lineKey = urlParams.get('ck_line_key');

    if (lineKey) {
      // Verify the line key exists in localStorage cart data
      const cartData = window.getTarteCartData?.();
      const itemExists = cartData?.items?.some(item => item.key === lineKey);

      if (itemExists) {
        this.editMode = true;
        this.editLineKey = lineKey;
      }
    }
  }

  /**
   * Remove a line item from the cart by key
   * @param {string} lineKey - The line item key to remove
   */
  async removeCartItem(lineKey) {
    try {
      await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      });
    } catch (e) {
      console.error('Error removing cart item:', e);
    }
  }

  attachShadeListDoneHandler() {
    if (this.shadeListDoneListener) return;

    this.shadeListDoneListener = (event) => {
      if (!window.isFromQvCustomkit) return;

      const button = event.target.closest('.done-button');
      if (!button) return;

      const customKitModal = document.querySelector('#CustomKit-Quickshop-Modal');
      const quickshopScope = customKitModal?.querySelector('#custom-kit-quickshop') || customKitModal;
      const isBagSpecial = window.bagSpecialQuickviewState?.requireQuickviewCta === true;

      if (!customKitModal || isBagSpecial) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const shadeModal = button.closest('modal-dialog');
      const selected = shadeModal?.querySelector('input[type="radio"][data-variant-id]:checked');

      if (!selected) {
        shadeModal?.hide?.();
        window.isFromQvCustomkit = false;
        return;
      }

      const variantId = selected.dataset.variantId;
      const isOOS =
        selected.closest('.shade-variant')?.classList.contains('oos') ||
        shadeModal.querySelector('.selected-shade-container.oos');

      if (isOOS) {
        this.showWaitlistForVariant(shadeModal, variantId);
        return;
      }

      if (!quickshopScope || !variantId) {
        shadeModal?.hide?.();
        window.isFromQvCustomkit = false;
        return;
      }

      const quickshopVariant = quickshopScope.querySelector(`input[type="radio"][data-variant-id="${variantId}"]`);
      if (quickshopVariant) {
        if (!quickshopVariant.checked) {
          quickshopVariant.checked = true;
        }
        quickshopVariant.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        const hiddenInput = quickshopScope.querySelector('product-info input[name="id"]');
        if (hiddenInput) {
          hiddenInput.value = variantId;
          hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      shadeModal?.hide?.();
      window.isFromQvCustomkit = false;

      if (this.currentModalProduct) {
        this.handleQuickshopAdd(customKitModal, this.currentModalProduct);
      }
    };

    document.addEventListener('click', this.shadeListDoneListener, true);
  }

  loadStrings() {
    const stringsScript = this.querySelector('script.custom-kit-strings');

    if (stringsScript) {
      try {
        this.strings = JSON.parse(stringsScript.textContent);
        console.log('Loaded custom kit strings:', this.strings);
      } catch (error) {
        console.error('Error parsing strings data:', error);
      }
    }
  }

  loadQuickshopStrings() {
    const quickshopStringsScript = document.querySelector('script.custom-kit-quickshop-strings');
    if (!quickshopStringsScript) return;

    try {
      this.quickshopStrings = JSON.parse(quickshopStringsScript.textContent);
    } catch (error) {
      console.error('Error parsing quickshop strings data:', error);
      this.quickshopStrings = {};
    }
  }

  loadCustomKitProduct() {
    const productScript = this.querySelector('script.custom-kit-product-data');

    if (productScript) {
      try {
        this.customKitProduct = JSON.parse(productScript.textContent);
        console.log('Loaded custom kit product:', this.customKitProduct);
      } catch (error) {
        console.error('Error parsing product data:', error);
      }
    } else {
      console.warn('No custom kit product data found. Please select a Custom Kit Product Variant in the theme editor.');
    }
  }

  loadCollections() {
    const collectionScripts = this.querySelectorAll('script.custom-kit-collection');

    this.collections = Array.from(collectionScripts).map(script => {
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('Error parsing collection data:', error);
        return null;
      }
    }).filter(Boolean);

    this.collections = this.applySingleVariantDuplicateInventoryCaps(this.collections);

    console.log('Loaded collections:', this.collections);
  }

  getCurrentCountry() {
    return String(window.Shopify?.country || 'US').toUpperCase();
  }

  normalizeDiscountType(value = this.settings?.discount_type) {
    return String(value || '').trim().toLowerCase();
  }

  isBuyOneGetOneDiscount() {
    return this.normalizeDiscountType() === 'buy one get one';
  }

  isPercentagePriceDisplayMode() {
    const discountType = this.normalizeDiscountType();
    return discountType === 'percentage' || discountType === 'buy one get one';
  }

  normalizeDiscountPercentage(value) {
    const percentage = Number(value);
    if (!Number.isFinite(percentage)) return 0;
    return Math.min(100, Math.max(0, percentage));
  }

  getConfigLocales(config) {
    if (Array.isArray(config?.locales)) {
      return config.locales
        .map(locale => String(locale || '').trim().toUpperCase())
        .filter(Boolean);
    }

    return String(config?.locales_string || '')
      .split(',')
      .map(locale => locale.trim().toUpperCase())
      .filter(Boolean);
  }

  getCustomKitConfigurationForCountry(country) {
    const countryCode = String(country || this.getCurrentCountry()).toUpperCase();
    const configurations = Array.isArray(this.settings?.custom_kit_configurations)
      ? this.settings.custom_kit_configurations
      : [];

    return configurations.find(config => this.getConfigLocales(config).includes(countryCode)) || null;
  }

  getConfiguredCustomKitCountries() {
    const countries = new Set([this.getCurrentCountry()]);
    const configurations = Array.isArray(this.settings?.custom_kit_configurations)
      ? this.settings.custom_kit_configurations
      : [];

    configurations.forEach(config => {
      this.getConfigLocales(config).forEach(country => countries.add(country));
    });

    return Array.from(countries);
  }

  getDiscountPercentageForCountry(country) {
    const countryConfig = this.getCustomKitConfigurationForCountry(country);
    return this.normalizeDiscountPercentage(
      countryConfig?.discount_percentage ?? this.settings?.discount_percentage
    );
  }

  getCollectionIndexById(collectionId) {
    const id = Number(collectionId);
    return this.collections.findIndex(collection => Number(collection.id) === id);
  }

  getCollectionIndexForTileIndex(tileIndex) {
    const targetIndex = Number(tileIndex);
    if (!Number.isFinite(targetIndex)) return -1;

    let globalTileIndex = 0;
    for (let collectionIndex = 0; collectionIndex < this.collections.length; collectionIndex++) {
      const collection = this.collections[collectionIndex];
      const requiredSelections = collection.required_selections || 1;
      const tileRangeEnd = globalTileIndex + requiredSelections;

      if (targetIndex >= globalTileIndex && targetIndex < tileRangeEnd) {
        return collectionIndex;
      }

      globalTileIndex = tileRangeEnd;
    }

    return -1;
  }

  getSelectedItemCategoryIndex(item) {
    const itemCategoryIndex = Number(item?.category_index);
    if (Number.isFinite(itemCategoryIndex)) return itemCategoryIndex;

    const product = this.findRawProductData(item?.product_id, item?.of_collection_id);
    const productCategoryIndex = Number(product?.category_index);
    if (Number.isFinite(productCategoryIndex)) return productCategoryIndex;

    return this.getCollectionIndexById(item?.of_collection_id);
  }

  getItemCategoryIndex(product, tileIndex) {
    const tileCategoryIndex = this.getCollectionIndexForTileIndex(tileIndex);
    if (tileCategoryIndex >= 0) return tileCategoryIndex;

    const productCategoryIndex = Number(product?.category_index);
    if (Number.isFinite(productCategoryIndex)) return productCategoryIndex;

    return this.getCollectionIndexById(product?.of_collection_id);
  }

  getBogoDiscountedPriceCents(priceCents, country, categoryIndex) {
    const cents = Number(priceCents) || 0;
    if (categoryIndex === 0) return cents;

    const discountPercentage = this.getDiscountPercentageForCountry(country);
    const multiplier = (100 - discountPercentage) / 100;
    return Math.round(cents * multiplier);
  }

  getBogoDiscountedAmount(amount, country, categoryIndex) {
    if (amount == null) return null;

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount < 0) return null;
    if (categoryIndex === 0) return numericAmount;

    const discountPercentage = this.getDiscountPercentageForCountry(country);
    return numericAmount * ((100 - discountPercentage) / 100);
  }

  formatPresentmentAmount(amount) {
    if (amount == null) return null;

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount < 0) return null;

    return (Math.round((numericAmount + Number.EPSILON) * 100) / 100).toFixed(2);
  }

  centsToPresentmentAmount(cents) {
    const numericCents = Number(cents);
    if (!Number.isFinite(numericCents) || numericCents < 0) return null;

    return numericCents / 100;
  }

  getItemDiscountPrice(product, variant, tileIndex) {
    if (this.isBuyOneGetOneDiscount()) {
      const categoryIndex = this.getItemCategoryIndex(product, tileIndex);
      const basePrice = variant?.price || product?.price || 0;
      return this.getBogoDiscountedPriceCents(basePrice, this.getCurrentCountry(), categoryIndex);
    }

    return product?.discount_price || product?.price || 0;
  }

  async fetchSelectedVariantAmountForCountry(item, country, options = {}) {
    const countryCode = String(country || this.getCurrentCountry()).toUpperCase();

    if (typeof window.fetchVariantPriceForCountry === 'function') {
      const fetchedAmount = await window.fetchVariantPriceForCountry(
        item.variant_id,
        countryCode,
        { useBatch: options.useBatch !== false }
      );
      const amount = fetchedAmount == null ? null : Number(fetchedAmount);
      if (Number.isFinite(amount) && amount > 0) {
        return amount;
      }
    }

    if (countryCode === this.getCurrentCountry()) {
      return this.centsToPresentmentAmount(item.variant_price);
    }

    return null;
  }

  getSelectedItemsBySlot() {
    return this.selectedItems
      .slice()
      .sort((a, b) => (Number(a.tileIndex) || 0) - (Number(b.tileIndex) || 0));
  }

  getSelectedItemSlot(item) {
    return String((Number(item?.tileIndex) || 0) + 1);
  }

  amountToCents(amount) {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount < 0) return null;

    return Math.round((numericAmount + Number.EPSILON) * 100);
  }

  async fetchSelectedVariantAmountsForCountry(items, country) {
    const countryCode = String(country || this.getCurrentCountry()).toUpperCase();
    const amountsBySlot = {};

    if (typeof window.fetchVariantPricesForCountry === 'function') {
      try {
        const variantPricesByGid = await window.fetchVariantPricesForCountry(
          items.map(item => item.variant_id),
          countryCode
        );

        items.forEach(item => {
          const slot = this.getSelectedItemSlot(item);
          const variantGid = window.toShopifyVariantGid(item.variant_id);
          const amount = Number(variantPricesByGid?.[variantGid]);

          if (Number.isFinite(amount) && amount >= 0) {
            amountsBySlot[slot] = amount;
          }
        });
      } catch (error) {
        console.warn(`[CK Pricing] Failed to fetch batched selected variant prices for ${countryCode}:`, error);
      }
    }

    await Promise.all(items.map(async (item) => {
      const slot = this.getSelectedItemSlot(item);
      if (Object.prototype.hasOwnProperty.call(amountsBySlot, slot)) return;

      const amount = await this.fetchSelectedVariantAmountForCountry(item, countryCode, { useBatch: false });
      if (amount != null) {
        amountsBySlot[slot] = amount;
      }
    }));

    return amountsBySlot;
  }

  getBogoProratedAmountsBySlotForCountry(items, country, regularAmountsBySlot) {
    const entries = [];

    for (const item of items) {
      const slot = this.getSelectedItemSlot(item);
      const regularAmount = Number(regularAmountsBySlot?.[slot]);

      if (!Number.isFinite(regularAmount) || regularAmount < 0) return null;

      const categoryIndex = this.getSelectedItemCategoryIndex(item);
      const discountedAmount = this.getBogoDiscountedAmount(regularAmount, country, categoryIndex);
      const regularCents = this.amountToCents(regularAmount);
      const discountedCents = this.amountToCents(discountedAmount);

      if (regularCents === null || discountedCents === null) return null;

      entries.push({
        slot,
        regularCents,
        discountedCents
      });
    }

    const targetCents = entries.reduce((total, entry) => total + entry.discountedCents, 0);
    const totalWeight = entries.reduce((total, entry) => total + entry.regularCents, 0);

    if (totalWeight <= 0) {
      if (targetCents !== 0) return null;

      return entries.reduce((amountsBySlot, entry) => {
        amountsBySlot[entry.slot] = 0;
        return amountsBySlot;
      }, {});
    }

    const allocations = entries.map((entry, index) => {
      const exactCents = (targetCents * entry.regularCents) / totalWeight;

      return {
        index,
        slot: entry.slot,
        cents: Math.floor(exactCents),
        remainder: exactCents - Math.floor(exactCents)
      };
    });

    let remainingCents = targetCents - allocations.reduce((total, allocation) => total + allocation.cents, 0);
    allocations
      .slice()
      .sort((a, b) => {
        if (b.remainder !== a.remainder) return b.remainder - a.remainder;
        return a.index - b.index;
      })
      .forEach(allocation => {
        if (remainingCents <= 0) return;

        allocation.cents += 1;
        remainingCents -= 1;
      });

    return allocations.reduce((amountsBySlot, allocation) => {
      amountsBySlot[allocation.slot] = allocation.cents / 100;
      return amountsBySlot;
    }, {});
  }

  async getBogoComponentPricingBySlot() {
    if (!this.isBuyOneGetOneDiscount() || !this.selectedItems.length) return {};

    const countries = this.getConfiguredCustomKitCountries();
    const selectedItems = this.getSelectedItemsBySlot();
    const pricingBySlot = {};

    selectedItems.forEach(item => {
      const slot = this.getSelectedItemSlot(item);

      pricingBySlot[slot] = {
        priceByCountry: {}
      };
    });

    await Promise.all(countries.map(async (country) => {
      const regularAmountsBySlot = await this.fetchSelectedVariantAmountsForCountry(selectedItems, country);
      const proratedAmountsBySlot = this.getBogoProratedAmountsBySlotForCountry(
        selectedItems,
        country,
        regularAmountsBySlot
      );

      if (!proratedAmountsBySlot) return;

      selectedItems.forEach(item => {
        const slot = this.getSelectedItemSlot(item);
        const formattedAmount = this.formatPresentmentAmount(proratedAmountsBySlot[slot]);

        if (formattedAmount === null) return;

        pricingBySlot[slot].priceByCountry[country] = formattedAmount;
      });
    }));

    return Object.entries(pricingBySlot).reduce((filteredPricingBySlot, [slot, pricing]) => {
      if (Object.keys(pricing.priceByCountry).length) {
        filteredPricingBySlot[slot] = pricing;
      }

      return filteredPricingBySlot;
    }, {});
  }

  getSingleVariantProductVariant(product) {
    return Array.isArray(product?.variants) && product.variants.length === 1
      ? product.variants[0]
      : null;
  }

  getSingleVariantInventoryLimit(product) {
    const variant = this.getSingleVariantProductVariant(product);
    return this.getVariantInventoryLimit(variant);
  }

  getVariantInventoryLimit(variant) {
    if (!variant) return Infinity;

    const inventoryPolicy = String(variant.inventory_policy || '').toLowerCase();
    if (inventoryPolicy === 'continue') return Infinity;

    if (!variant.inventory_management) return Infinity;

    const inventoryQuantity = Number(variant.inventory_quantity);
    return Number.isFinite(inventoryQuantity)
      ? Math.max(0, inventoryQuantity)
      : Infinity;
  }

  getProductVariant(product, variantId) {
    const id = Number(variantId);
    return product?.variants?.find(variant => Number(variant.id) === id) || null;
  }

  getSingleVariantInventoryKey(product) {
    const variant = this.getSingleVariantProductVariant(product);
    return variant?.id ? String(variant.id) : null;
  }

  withSingleVariantUnavailable(product) {
    const variant = this.getSingleVariantProductVariant(product);
    if (!variant) return product;

    return {
      ...product,
      available: false,
      inventory_capped: true,
      variants: [
        {
          ...variant,
          available: false,
          inventory_capped: true
        }
      ]
    };
  }

  applySingleVariantDuplicateInventoryCaps(collections) {
    const seenByVariant = new Map();

    return collections.map(collection => {
      if (!Array.isArray(collection?.products)) return collection;

      return {
        ...collection,
        products: collection.products.map(product => {
          const key = this.getSingleVariantInventoryKey(product);
          const limit = this.getSingleVariantInventoryLimit(product);

          if (!key || !Number.isFinite(limit)) {
            return product;
          }

          const seen = seenByVariant.get(key) || 0;
          seenByVariant.set(key, seen + 1);

          return seen < limit ? product : this.withSingleVariantUnavailable(product);
        })
      };
    });
  }

  getSelectedVariantCount(variantId, excludedTileIndex = null) {
    const id = Number(variantId);
    return this.selectedItems.filter(item => {
      if (Number(item?.variant_id) !== id) return false;
      return excludedTileIndex == null || Number(item?.tileIndex) !== Number(excludedTileIndex);
    }).length;
  }

  getCartVariantCount(variantId) {
    const id = Number(variantId);
    if (!Number.isFinite(id)) return 0;
    return this.cartVariantQuantities.get(String(id)) || 0;
  }

  getReservedVariantCount(variantId, excludedTileIndex = null) {
    return this.getCartVariantCount(variantId) + this.getSelectedVariantCount(variantId, excludedTileIndex);
  }

  getCartLineKeyToExclude() {
    if (this.editLineKey) return this.editLineKey;

    try {
      return new URLSearchParams(window.location.search).get('ck_line_key');
    } catch (error) {
      return null;
    }
  }

  getCartLineBundleKey(cartItems, lineKey) {
    if (!lineKey || !Array.isArray(cartItems)) return null;
    const line = cartItems.find(item => item?.key === lineKey);
    return line?.properties?._bundle_key || line?.bundle_key || null;
  }

  getVariantIdFromValue(value) {
    if (value == null || value === '') return null;

    const numericId = Number(value);
    if (Number.isFinite(numericId)) return numericId;

    const match = String(value).match(/ProductVariant\/(\d+)/);
    return match ? Number(match[1]) : null;
  }

  parseBundleComponentsProperty(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  getCartItemComponentVariantId(component) {
    return this.getVariantIdFromValue(
      component?.variant_id ||
      component?.variantId ||
      component?.merchandiseId ||
      component?.id ||
      component?.variant?.id
    );
  }

  incrementVariantCount(counts, variantId, quantity = 1) {
    const id = this.getVariantIdFromValue(variantId);
    const qty = Math.max(0, Number(quantity) || 0);
    if (!id || qty <= 0) return;

    const key = String(id);
    counts.set(key, (counts.get(key) || 0) + qty);
  }

  buildCartVariantQuantityMap(cartData) {
    const counts = new Map();
    const cartItems = Array.isArray(cartData?.items) ? cartData.items : [];
    const excludedLineKey = this.getCartLineKeyToExclude();
    const excludedBundleKey = this.getCartLineBundleKey(cartItems, excludedLineKey);

    cartItems.forEach(item => {
      const itemBundleKey = item?.properties?._bundle_key || item?.bundle_key || null;
      if (excludedLineKey && item?.key === excludedLineKey) return;
      if (excludedBundleKey && itemBundleKey === excludedBundleKey) return;

      if (Array.isArray(item?.item_components) && item.item_components.length) {
        item.item_components.forEach(component => {
          this.incrementVariantCount(
            counts,
            this.getCartItemComponentVariantId(component),
            component?.quantity || 1
          );
        });
        return;
      }

      const bundleComponents = this.parseBundleComponentsProperty(item?.properties?._bundle_components);
      if (bundleComponents.length) {
        const lineQuantity = Math.max(1, Number(item?.quantity) || 1);
        bundleComponents.forEach(component => {
          const componentQuantity = Math.max(1, Number(component?.quantity) || 1);
          this.incrementVariantCount(
            counts,
            this.getCartItemComponentVariantId(component),
            componentQuantity * lineQuantity
          );
        });
        return;
      }

      this.incrementVariantCount(counts, item?.variant_id, item?.quantity || 1);
    });

    return counts;
  }

  async getCurrentCartData(options = {}) {
    try {
      if (typeof window.getCurrentTarteCartData === 'function') {
        return await window.getCurrentTarteCartData(options);
      }

      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { cache: 'no-store' });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Custom Kit: Error fetching cart data:', error);
      return null;
    }
  }

  async refreshCartVariantQuantities(options = {}) {
    const cartData = await this.getCurrentCartData(options);
    this.cartVariantQuantities = this.buildCartVariantQuantityMap(cartData);
    return this.cartVariantQuantities;
  }

  getInventoryAdjustedGridProducts(collection) {
    if (!Array.isArray(collection?.products)) return [];
    const seenByVariant = new Map();

    return collection.products.map(product => {
      const variant = this.getSingleVariantProductVariant(product);
      const limit = this.getSingleVariantInventoryLimit(product);

      if (!variant || !Number.isFinite(limit)) {
        return product;
      }

      const key = String(variant.id);
      const seen = seenByVariant.get(key) || 0;
      seenByVariant.set(key, seen + 1);

      const reservedCount = this.getReservedVariantCount(variant.id);
      if (reservedCount + seen >= limit) {
        return this.withSingleVariantUnavailable(product);
      }

      return product;
    });
  }

  isVariantAvailableWithinInventory(product, variant, options = {}) {
    if (!variant || variant.available === false) return false;

    const limit = this.getVariantInventoryLimit(variant);
    if (!Number.isFinite(limit)) return true;

    return this.getReservedVariantCount(variant.id, options.excludedTileIndex) < limit;
  }

  canAddVariantWithinInventory(product, variant, options = {}) {
    if (!product || !variant) return false;

    if (product.available === false || variant.available === false) {
      return false;
    }

    return this.isVariantAvailableWithinInventory(product, variant, options);
  }

  getCustomKitVtoInventoryOptions(targetProduct = null) {
    if (!targetProduct) {
      return { excludedTileIndex: null };
    }

    return this.getQuickshopInventoryOptions({
      isEditMode: this.isEditMode,
      existingTileIndex: this.editingTileIndex
    });
  }

  buildCustomKitVtoVariant(product, variant, inventoryOptions = {}) {
    const baseAvailable = product?.available !== false && variant?.available !== false;
    const effectiveAvailable = baseAvailable && this.isVariantAvailableWithinInventory(product, variant, inventoryOptions);

    return {
      id: variant.id,
      title: variant.title,
      barcode: variant.barcode,
      available: effectiveAvailable,
      raw_available: variant.available,
      inventory_capped: !effectiveAvailable && baseAvailable,
      price: variant.price,
      featured_image: variant.featured_image?.url || variant.featured_image,
      swatch_image: variant.swatch_image?.url || variant.swatch_image || ''
    };
  }

  applySelectedDuplicateInventoryCaps() {
    const selectedByVariant = new Map(this.cartVariantQuantities);

    this.selectedItems
      .filter(Boolean)
      .sort((a, b) => (a.tileIndex ?? 0) - (b.tileIndex ?? 0))
      .forEach(item => {
        const product = this.findRawProductData(item.product_id, item.of_collection_id);
        const variant = product?.variants?.find(productVariant => Number(productVariant.id) === Number(item.variant_id));

        if (product?.available === false || variant?.available === false) {
          item.variant_available = false;
        }

        const limit = this.getVariantInventoryLimit(variant);
        if (!Number.isFinite(limit)) return;

        const key = String(item.variant_id);
        const selectedCount = selectedByVariant.get(key) || 0;
        selectedByVariant.set(key, selectedCount + 1);

        if (selectedCount >= limit) {
          item.variant_available = false;
        }
      });
  }

  getQuickshopInventoryOptions(options = {}) {
    const isEditMode = options.isEditMode ?? this.isEditMode;
    return {
      excludedTileIndex: isEditMode
        ? (options.existingTileIndex ?? this.editingTileIndex ?? null)
        : null
    };
  }

  syncProductInfoVariantInventory(productInfo, product, options = {}) {
    if (!productInfo || !Array.isArray(productInfo.variantData) || !Array.isArray(product?.variants)) {
      return;
    }

    productInfo.variantData = productInfo.variantData.map(variantData => {
      const productVariant = this.getProductVariant(product, variantData.id);
      if (!productVariant) return variantData;

      return {
        ...variantData,
        available: this.isVariantAvailableWithinInventory(product, productVariant, options)
      };
    });
  }

  applyQuickshopVariantInventoryCaps(scope, product, options = {}) {
    if (!scope || !Array.isArray(product?.variants)) return;

    const productInfo = scope.querySelector?.('product-info') || scope.closest?.('product-info') || null;
    const inventoryOptions = this.getQuickshopInventoryOptions(options);
    this.syncProductInfoVariantInventory(productInfo, product, inventoryOptions);

    if (productInfo && typeof productInfo.updateSwatchAvailability === 'function') {
      const selectedOptions = {};
      productInfo.querySelectorAll('.product-variant-picker fieldset').forEach((fieldset, index) => {
        const checkedInput = fieldset.querySelector('input:checked');
        selectedOptions[index] = checkedInput?.value || null;
      });
      productInfo.updateSwatchAvailability(selectedOptions);
    }

    const inputs = (productInfo || scope).querySelectorAll('input[type="radio"][data-variant-id]');
    inputs.forEach(input => {
      const variant = this.getProductVariant(product, input.dataset.variantId);
      if (!variant) return;

      if (input.dataset.ckBaseVariantAvailable == null) {
        input.dataset.ckBaseVariantAvailable = input.dataset.variantAvailable || (variant.available === false ? 'false' : 'true');
      }
      if (input.dataset.ckBaseUnavailableMessage == null && input.hasAttribute('data-unavailable-message')) {
        input.dataset.ckBaseUnavailableMessage = input.getAttribute('data-unavailable-message') || '';
      }

      const baseAvailable = input.dataset.ckBaseVariantAvailable !== 'false' && variant.available !== false;
      const effectiveAvailable = baseAvailable && this.isVariantAvailableWithinInventory(product, variant, inventoryOptions);
      const wasInventoryCapped = input.dataset.ckInventoryCapped === 'true';

      input.dataset.variantAvailable = effectiveAvailable ? 'true' : 'false';
      input.dataset.ckInventoryCapped = !effectiveAvailable && baseAvailable ? 'true' : 'false';
      input.classList.toggle('visually-disabled', !effectiveAvailable);
      input.classList.toggle('shade--not-available', !effectiveAvailable);

      if (!effectiveAvailable) {
        const message = input.dataset.ckBaseUnavailableMessage || window.variantStrings?.unavailable || 'out of stock';
        input.setAttribute('data-unavailable-message', message);
      } else if (wasInventoryCapped && !input.dataset.ckBaseUnavailableMessage) {
        input.removeAttribute('data-unavailable-message');
      }
    });
  }

  render() {
    console.log('Custom Kit initialized with', this.collections.length, 'collections');

    // Preserve script tags and generate HTML
    this.destroyHeaderSwiper();
    const scripts = Array.from(this.querySelectorAll('script'));

    const headerHTML = this.createHeaderHTML();
    const collectionsHTML = this.createCollectionsHTML();

    this.innerHTML = `
      ${headerHTML}
      <div class="custom-kit-collections-wrapper page-width">
        ${collectionsHTML}
      </div>
    `;

    // Re-append script tags
    scripts.forEach(script => this.appendChild(script));

    this.initHeaderSwiper();
    this.cacheHeaderDom();

    this.renderStarRatings();

    this.animateSavingsBannerOnTileChange();

    // Run pending animation if scheduled, otherwise handle any pending scroll
    if (this.pendingAnimation) {
      this.animateGrid();
    } else {
      this.scrollToTarget();
    }

    // Re-apply scroll styles after render (for mobile minified header)
    this.handleScroll();

    // Hide skeleton immediately, but wait for images before removing min-height
    this.classList.add('ck-skeleton-hidden');
    this.waitForImagesToLoad();
  }

  /**
   * Wait for visible images to load before removing min-height constraint
   * Uses a timeout fallback to prevent indefinite waiting
   */
  waitForImagesToLoad() {
    const images = this.querySelectorAll('.custom-kit-product-card img, .custom-kit-content-slot img');
    if (images.length === 0) {
      this.classList.add('ck-rendered');
      return;
    }

    let loadedCount = 0;
    const totalImages = images.length;
    const onImageReady = () => {
      loadedCount++;
      if (loadedCount >= totalImages) {
        this.classList.add('ck-rendered');
      }
    };

    images.forEach(img => {
      if (img.complete) {
        onImageReady();
      } else {
        img.addEventListener('load', onImageReady, { once: true });
        img.addEventListener('error', onImageReady, { once: true });
      }
    });

    // Fallback timeout in case images take too long or fail silently
    setTimeout(() => {
      if (!this.classList.contains('ck-rendered')) {
        this.classList.add('ck-rendered');
      }
    }, 3000);
  }

  /**
   * Check if the maximum number of custom kits in cart has been reached
   * @param {boolean} showAlert - Whether to show an alert to the customer
   * @returns {Promise<boolean>} - True if max kits reached, false otherwise
   */
  async isCustomKitMaxReached(showAlert = false) {
    const maxKits = this.customKitProduct?.max_quantity;
    const customKitProductId = this.customKitProduct?.product_id;

    if (!maxKits || maxKits <= 0 || !customKitProductId) {
      return false;
    }

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      const cartData = await response.json();

      const uniqueBundleKeys = new Set();

      cartData.items.forEach(item => {
        // Check if this item is part of a custom kit bundle with matching product ID
        const bundleProductId = item.properties?._bundle_product_id;
        const bundleKey = item.properties?._bundle_key;

        if (bundleProductId && bundleKey && String(bundleProductId) === String(customKitProductId)) {
          uniqueBundleKeys.add(bundleKey);
        } else if (item.product_id === customKitProductId) {
          uniqueBundleKeys.add(item.key || item.id);
        }
      });

      const customKitCount = uniqueBundleKeys.size;
      const isMaxReached = customKitCount >= maxKits;

      if (isMaxReached && showAlert) {
        const alertMessage = this.strings.max_kits_reached || `You can only have ${maxKits} custom kit(s) in your cart.`;
        alert(alertMessage);
      }

      return isMaxReached;
    } catch (error) {
      console.error('Error checking custom kit limit:', error);
      return false;
    }
  }

  /**
   * Schedule animation if collection index changed
   * @param {number} previousIndex - Previous collection index
   * @param {number} [previousTileIndex] - Previous tile index (optional, defaults to this.previousHeaderTileIndex)
   * @param {Object} [options] - Animation options
   */
  scheduleAnimationIfChanged(previousIndex, previousTileIndex, options = {}) {
    if (previousIndex !== this.currentCollectionIndex) {
      const { allowScrollToTop = true } = options;
      this.pendingAnimation = {
        fromIndex: previousIndex,
        toIndex: this.currentCollectionIndex,
        previousTileIndex: previousTileIndex ?? this.previousHeaderTileIndex,
        allowScrollToTop
      };
    }
  }

  /**
   * Animate the grid transition
   */
  animateGrid() {
    // Clear any existing animation timeout
    if (this.animationTimeoutId) {
      clearTimeout(this.animationTimeoutId);
      this.animationTimeoutId = null;
    }

    const { fromIndex, toIndex, previousTileIndex, allowScrollToTop } = this.pendingAnimation;
    const fromContainer = this.querySelector(`.custom-kit-collection-container[data-collection-index="${fromIndex}"]`);
    const toContainer = this.querySelector(`.custom-kit-collection-container[data-collection-index="${toIndex}"]`);

    if (!fromContainer || !toContainer) return;

    const slideLeft = toIndex > fromIndex;

    if (allowScrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // After delay, start slide transition with overlay fade
    this.animationTimeoutId = setTimeout(() => {
      // Clear pending animation
      this.pendingAnimation = null;

      // Remove loading class and start slide animations (ck-slide-out triggers overlay fade out)
      fromContainer.classList.remove('ck-loading');
      fromContainer.classList.add('ck-slide-out', slideLeft ? 'ck-slide-left' : 'ck-slide-right');
      toContainer.classList.add('active', 'ck-slide-in', slideLeft ? 'ck-slide-from-right' : 'ck-slide-from-left');

      // Update active tile and collection header mid-transition (when grids are crossing)
      setTimeout(() => {
        this.updateActiveTileDOM(previousTileIndex);
        this.updateActiveCollectionHeaderDOM(fromIndex);
      }, 400);

      // Clean up after animation completes (0.9s)
      setTimeout(() => {
        fromContainer.classList.remove('active', 'ck-slide-out', 'ck-slide-left', 'ck-slide-right');
        toContainer.classList.remove('ck-slide-in', 'ck-slide-from-right', 'ck-slide-from-left');
        this.animationTimeoutId = null;
        this.scrollToTarget();
      }, 900);
    }, 300); // Delay while overlay is visible
  }

  animateSavingsBannerOnTileChange() {
    const banner = this.querySelector('.custom-kit-savings-banner');
    if (!banner) return;

    // Don't animate in static mode; ensure loading state is cleared if present
    if (this.isStaticSavingsMode()) {
      banner.classList.remove('ck-loading');
      if (this.dataset.savingsBannerTimeoutId) {
        clearTimeout(Number(this.dataset.savingsBannerTimeoutId));
        delete this.dataset.savingsBannerTimeoutId;
      }
      if (this.savingsBannerContentTimeoutId) {
        clearTimeout(this.savingsBannerContentTimeoutId);
        this.savingsBannerContentTimeoutId = null;
      }
      this.dataset.activeTileIndex = String(this.currentHeaderTileIndex);
      return;
    }

    const currentIndex = String(this.currentHeaderTileIndex);

    // Check if content changed
    const newContent = banner.innerHTML;
    const contentChanged = this.previousSavingsBannerContent !== undefined &&
                           this.previousSavingsBannerContent !== newContent;

    if (contentChanged) {
      if (this.dataset.savingsBannerTimeoutId) {
        clearTimeout(Number(this.dataset.savingsBannerTimeoutId));
      }
      if (this.savingsBannerContentTimeoutId) {
        clearTimeout(this.savingsBannerContentTimeoutId);
      }

      // Restore previous content for delayed update
      if (this.previousSavingsBannerContent !== undefined) {
        banner.innerHTML = this.previousSavingsBannerContent;
      }

      // Update content after 800ms to match CSS transition delay
      this.savingsBannerContentTimeoutId = setTimeout(() => {
        const currentBanner = this.querySelector('.custom-kit-savings-banner');
        if (currentBanner) {
          currentBanner.innerHTML = newContent;
        }
        this.savingsBannerContentTimeoutId = null;
      }, 800);

      banner.classList.add('ck-loading');
      const timeoutId = setTimeout(() => {
        const currentBanner = this.querySelector('.custom-kit-savings-banner');
        currentBanner?.classList.remove('ck-loading');
        delete this.dataset.savingsBannerTimeoutId;
      }, 1600);

      this.dataset.savingsBannerTimeoutId = String(timeoutId);
    }

    // Store current content for next change
    this.previousSavingsBannerContent = newContent;

    this.dataset.activeTileIndex = currentIndex;
  }

  scrollToTarget() {
    const openPendingQuickview = () => {
      if (this.pendingQuickview) {
        const { product, variantId, tileIndex } = this.pendingQuickview;
        this.pendingQuickview = null;
        this.openCustomKitQuickShop(product, true, variantId, tileIndex);
      }
    };

    if (!this.pendingScrollTarget) {
      // No scroll needed, open quickview immediately
      openPendingQuickview();
      return;
    }

    const { productId, collectionId } = this.pendingScrollTarget;
    const targetCard = this.querySelector(`.custom-kit-product-card[data-product-id="${productId}"][data-collection-id="${collectionId}"]`);
    this.pendingScrollTarget = null;

    if (!targetCard) {
      openPendingQuickview();
      return;
    }

    // Check if element is already near center of viewport (no scroll needed)
    const rect = targetCard.getBoundingClientRect();
    const viewportCenter = window.innerHeight / 2;
    const elementCenter = rect.top + rect.height / 2;

    if (Math.abs(elementCenter - viewportCenter) < 150) {
      // Already in view, open quickview immediately
      openPendingQuickview();
      return;
    }

    // Start scroll and wait for it to complete before opening quickview
    requestAnimationFrame(() => {
      targetCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.waitForScrollEnd(openPendingQuickview);
    });
  }

  /**
   * Wait for scroll to end, then execute callback
   */
  waitForScrollEnd(callback) {
    if ('onscrollend' in window) {
      window.addEventListener('scrollend', callback, { once: true });
    } else {
      // Fallback: poll for scroll stability
      let lastPos = window.scrollY;
      const check = () => {
        if (window.scrollY === lastPos) return callback();
        lastPos = window.scrollY;
        setTimeout(check, 30);
      };
      setTimeout(check, 30);
    }
  }

  /**
   * Update the active tile DOM element to reflect current currentHeaderTileIndex
   * @param {number} previousTileIndex - The previous tile index to remove active from
   */
  updateActiveTileDOM(previousTileIndex) {
    // Remove active from previous tile
    if (previousTileIndex !== undefined && previousTileIndex !== this.currentHeaderTileIndex) {
      const previousTile = this.querySelector(`.custom-kit-header-tile[data-tile-index="${previousTileIndex}"]`);
      if (previousTile) {
        previousTile.classList.remove('active');
      }
    }

    // Add active to current tile
    const currentTile = this.querySelector(`.custom-kit-header-tile[data-tile-index="${this.currentHeaderTileIndex}"]`);
    if (currentTile) {
      currentTile.classList.add('active');
    }

    // Update plus icon to match new active tile
    this.handleScroll();
  }

  /**
   * Update the active collection header to reflect current collection
   * @param {number} previousIndex - The previous collection index
   */
  updateActiveCollectionHeaderDOM(previousIndex) {
    // Remove active from previous collection header
    if (previousIndex !== undefined && previousIndex !== this.currentCollectionIndex) {
      const previousHeader = this.querySelector(`.custom-kit-collection-header[data-collection-index="${previousIndex}"]`);
      if (previousHeader) {
        previousHeader.classList.remove('active');
        // Reset scroll styles on previous header
        previousHeader.style.marginTop = '';
        const prevCounter = previousHeader.querySelector('.custom-kit-collection-item-counter');
        const prevTitle = previousHeader.querySelector('.custom-kit-collection-title');
        if (prevCounter) prevCounter.style.opacity = '';
        if (prevTitle) {
          prevTitle.style.opacity = '';
          prevTitle.style.transform = '';
        }
      }
    }

    // Add active to current collection header
    const currentHeader = this.querySelector(`.custom-kit-collection-header[data-collection-index="${this.currentCollectionIndex}"]`);
    if (currentHeader) {
      currentHeader.classList.add('active');
    }

    // Re-apply scroll styles to new active header
    this.handleScroll();
  }

  /**
   * Bind all event listeners using event delegation (called once in init)
   */
  bindEventListeners() {
    // Event delegation for product card clicks
    this.addEventListener('click', (e) => {
      // Handle deselect via explicit checkmark click
      const checkmark = e.target.closest('.selected-item-checkmark-icon');
      if (checkmark) {
        const productCard = e.target.closest('.custom-kit-product-card');
        if (productCard) {
          const productId = productCard.dataset.productId;
          const collectionId = productCard.dataset.collectionId;
          const selectedItem = this.findSelectedItemByProductId(productId, collectionId);
          if (selectedItem) {
            this.removeItem(selectedItem.variant_id, selectedItem.of_collection_id);
            this.render();
            return;
          }
        }
      }

      // Handle add to bag button clicks
      const addToBagButton = e.target.closest('.custom-kit-add-to-bag');
      if (addToBagButton) {
        this.handleAddCustomKitToBag();
        return;
      }

      const ratingWrapper = e.target.closest('.custom-kit__ratings-wrapper');
      if (ratingWrapper) {
        if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        const parentCard = ratingWrapper.closest('.custom-kit-product-card');
        const productId = parentCard?.dataset?.productId;
        const collectionId = parentCard?.dataset?.collectionId;
        const product = this.findRawProductData(productId, collectionId);
        const selectedItem = this.findSelectedItemByProductId(productId, collectionId);

        if (product) {
          this.openQuickshopAndShowReviews(product, !!selectedItem, selectedItem?.variant_id, selectedItem?.tileIndex);
        }
        return;
      }

      // Handle product card element clicks (image wrapper, title, or price)
      const imageWrapper = e.target.closest('.custom-kit-product-image-wrapper');
      const productTitle = e.target.closest('.custom-kit-product-title');
      const productPrice = e.target.closest('.custom-kit-product-price-container');

      if (imageWrapper || productTitle || productPrice) {
        const productCard = e.target.closest('.custom-kit-product-card');
        if (productCard) {
          const productId = productCard.dataset.productId;
          const collectionId = productCard.dataset.collectionId;
          const isSelected = productCard.classList.contains('selected');

          if (!isSelected) {
            const { canAdd } = this.canAddToCollection(collectionId);
            if (!canAdd) {
              return;
            }
          }

          const product = this.findRawProductData(productId, collectionId);
          if (product) {
            // Determine interaction type
            const clickedElement = imageWrapper ? 'image' : (productTitle ? 'title' : 'price');
            this.handleProductCardClick(product, clickedElement);
          }
        }
        return;
      }

      // Handle remove button clicks in header tiles
      const removeButton = e.target.closest('.custom-kit-header-tile-remove');
      if (removeButton) {
        e.stopPropagation();
        const tileIndex = parseInt(removeButton.dataset.tileIndex);
        if (!isNaN(tileIndex)) {
          this.removeItemByTileIndex(tileIndex);
        }
        this.render();
        return;
      }

      // Handle edit button clicks in header tiles
      const editButton = e.target.closest('.custom-kit-header-tile-edit');
      if (editButton) {
        e.stopPropagation();
        const tileIndex = parseInt(editButton.dataset.tileIndex);
        if (!isNaN(tileIndex)) {
          this.handleFilledTileClick(tileIndex);
        }
        return;
      }

      // Handle header tile clicks (to switch collection)
      const headerTile = e.target.closest('.custom-kit-header-tile');
      if (headerTile) {
        const tileIndex = parseInt(headerTile.dataset.tileIndex);
        if (tileIndex !== undefined && !isNaN(tileIndex)) {
          this.handleFilledTileClick(tileIndex);
        }
        return;
      }

      // Handle VTO modal opener clicks - set up multi-product data
      const vtoOpener = e.target.closest('[data-multi-product-vto]');
      if (vtoOpener) {
        this.setMultiProductVtoData();
      }
    });

    document.addEventListener('custom-kit:add-item-from-vto', (event) => {
      this.handleAddFromVto(event.detail || {});
    });
  }

  /**
   * Set up window.__multiVtoProducts with VTO-enabled products from current collection
   * @param {Object|null} targetProduct - Optional product to make active in the carousel
   */
  setMultiProductVtoData(targetProduct = null) {
    const currentCollection = this.collections[this.currentCollectionIndex];
    if (!currentCollection) {
      window.__multiVtoProducts = [];
      window.__multiVtoMeta = null;
      return;
    }

    // Mirror grid filtering so VTO only sees products currently displayed
    const filteredProducts = this.getGridProducts(currentCollection);

    const inventoryOptions = this.getCustomKitVtoInventoryOptions(targetProduct);

    // Filter products that have VTO enabled (vto_type is set), exclude bundles,
    // and drop products with no variants available for the current kit state.
    const vtoProducts = filteredProducts
      .filter(p => p.vto_type && p.available && !p.is_bundle)
      .map(product => ({
        product,
        variants: product.variants.map(variant =>
          this.buildCustomKitVtoVariant(product, variant, inventoryOptions)
        )
      }))
      .filter(({ variants }) => variants.some(variant => variant.available));

    // Map to the format expected by vto-modal.js
    window.__multiVtoProducts = vtoProducts.map(({ product, variants }) => ({
      id: product.id,
      handle: product.handle,
      title: product.title,
      featured_image: product.featured_image?.url || '',
      vto_type: product.vto_type,
      default_variant_id: variants.some(variant =>
        Number(variant.id) === Number(product.default_variant_id) && variant.available
      )
        ? product.default_variant_id
        : variants.find(variant => variant.available)?.id,
       // data needed for add-to-kit flow
      url: product.url,
      price: product.price,
      discount_price: product.discount_price || product.price,
      compare_at_price: product.compare_at_price,
      available: product.available,
      of_collection_id: currentCollection.id,
      of_collection_handle: currentCollection.handle,
      variants
    }));

    // Set metadata for VTO modal
    window.__multiVtoMeta = {
      fromCustomKit: true,
      collectionId: currentCollection.id,
      collectionHandle: currentCollection.handle,
      // Keep multi-product context when reopening the VTO from the same quickview
      persistAfterClose: this.settings?.enable_quickview_multi_vto === true,
      ...(targetProduct && {
        targetProductId: targetProduct.id,
        targetProductHandle: targetProduct.handle
      })
    };
  }

  handleAddFromVto({ productId, variantId, collectionId }) {
    const targetCollectionId = Number(collectionId) || this.collections[this.currentCollectionIndex]?.id;
    const product = this.findRawProductData(productId, targetCollectionId);
    if (!product) {
      return;
    }

    const variant = product.variants.find(v => Number(v.id) === Number(variantId));
    if (!variant) {
      return;
    }

    const { canAdd } = this.canAddToCollection(targetCollectionId);
    if (!canAdd) {
      const activeTileIndex = this.currentHeaderTileIndex;
      const existingItem = this.selectedItems.find(item => item.tileIndex === activeTileIndex);

      if (existingItem) {
        this.removeItemByTileIndex(existingItem.tileIndex);
        this.addItem(product, variant);
        this.render();
      }
      return;
    }

    const added = this.addItem(product, variant);
    if (added) {
      this.render();
    }
  }

  /**
   * Handle click on a filled tile (or edit button) - updates indices, scrolls to product, then opens quickview
   * @param {number} tileIndex - The tile index that was clicked
   */
  handleFilledTileClick(tileIndex) {
    // Store current values before updating (for tile clicks, savings don't change)
    const currentTileIndex = this.currentHeaderTileIndex;

    // Update indices and schedule animation if collection changed
    const previousIndex = this.updateCurrentIndicesByHeaderTileIndex(tileIndex);
    this.scheduleAnimationIfChanged(previousIndex, currentTileIndex, { allowScrollToTop: false });

    // If tile has a selected item, scroll to it first, then open quickview after scroll completes
    const selectedItem = this.selectedItems.find(item => item.tileIndex === tileIndex);
    if (selectedItem) {
      // Scroll to the product after render/animation
      this.pendingScrollTarget = {
        productId: selectedItem.product_id,
        collectionId: selectedItem.of_collection_id
      };

      // Store quickview data to open after scroll completes
      const product = this.findRawProductData(selectedItem.product_id, selectedItem.of_collection_id);
      if (product) {
        this.pendingQuickview = { product, variantId: selectedItem.variant_id, tileIndex: selectedItem.tileIndex };
        // Start prefetching product content immediately so it's ready when scroll ends
        this.prefetchPromise = fetch(product.url).then(r => r.text()).catch(() => null);
      }
    } else {
      this.pendingScrollTarget = null;
      this.pendingQuickview = null;
      this.prefetchPromise = null;
    }

    this.render();

    // Re-run scroll handling so minified-fit state (and swiper disable) is reapplied after re-render
    requestAnimationFrame(() => this.handleScroll());
  }

  /**
   * Create HTML for the header with selected items
   * @returns {string} - The header HTML
   */
  createHeaderHTML() {
    const tilesHTML = this.createHeaderTilesHTML();
    const savingsBannerHTML = this.createSavingsBannerHTML();
    const addToBagText = this.getAddToBagText();
    const headerTitle = this.settings?.progress_header_title || '';
    const addToBagStateClass = this.getAddToBagStateClass();
    const bgColor = this.settings?.progress_header_background_color || '';
    const bgStyle = `style="background-color: ${bgColor || '#fdf9f9'};"`;
    const collectionHeadersHTML = this.createAllCollectionHeadersHTML();
    // Disable button if in OOS edit mode (has OOS item or had one auto-removed)
    const isDisabled = this.isInOosEditMode();
    const disabledAttr = isDisabled ? ' disabled' : '';

    return `
      <div class="custom-kit-header page-width" ${bgStyle}>
        <div class="custom-kit-header-info">
          ${savingsBannerHTML}
          ${headerTitle ? `<h2 class="custom-kit-header-title">${headerTitle}</h2>` : ''}
        </div>
        <div class="custom-kit-header-tiles swiper">
          <div class="swiper-wrapper">
            ${tilesHTML}
          </div>
          <div class="swiper-button-prev" aria-label="Previous selection"></div>
          <div class="swiper-button-next" aria-label="Next selection"></div>
        </div>
        <button class="cta-button primary custom-kit-add-to-bag${addToBagStateClass}" type="button"${disabledAttr}>${addToBagText}</button>
        ${collectionHeadersHTML}
      </div>
    `;
  }

  /**
   * Create HTML for all collection headers (one per collection)
   * Visibility is controlled via CSS based on active collection
   * @returns {string} - All collection headers HTML
   */
  createAllCollectionHeadersHTML() {
    const activeIndex = this.pendingAnimation ? this.pendingAnimation.fromIndex : this.currentCollectionIndex;

    return this.collections.map((collection, index) => {
      const collectionTitleHTML = this.getCollectionTitleHTML(collection);
      const selectedCount = this.getCollectionSelectionCount(collection.id);
      const requiredCount = collection.required_selections || 1;
      const isComplete = selectedCount >= requiredCount;
      const counterClass = isComplete ? ' complete' : '';
      const activeClass = index === activeIndex ? ' active' : '';

      return `
        <div class="custom-kit-collection-header${activeClass}" data-collection-index="${index}" data-collection-id="${collection.id}">
          ${collectionTitleHTML}
          <div class="custom-kit-collection-item-counter${counterClass}"><span class="selected-count">${selectedCount}</span>/<span class="required-count">${requiredCount}</span></div>
        </div>
      `;
    }).join('');
  }

  /**
   * Get the appropriate add to bag button text based on edit mode and requirements
   * @param {boolean} allRequirementsMet - Whether all kit requirements are met
   * @returns {string} - The button text
   */
  getAddToBagText() {
    if (this.editMode) {
      // If editing a kit with OOS items (or had one auto-removed), always show "UPDATE BAG"
      if (this.isInOosEditMode()) {
        return this.strings.update_bag || 'UPDATE BAG';
      }
      return this.allRequirementsMet && this.itemWasUpdated
        ? this.strings.update_bag || 'UPDATE BAG'
        : this.strings.back_to_bag || 'BACK TO BAG';
    }
    return this.strings.add_to_bag || 'ADD TO BAG';
  }

  getAddToBagStateClass() {
    const wasVisible = this.wasAddToBagVisible;
    let stateClass = '';

    if (this.allRequirementsMet || this.editMode) {
      // Skip reveal animation if button was already visible
      stateClass = wasVisible ? ' btn-visible' : '';
      this.wasAddToBagVisible = true;
    } else {
      stateClass = wasVisible ? ' btn-hiding' : ' btn-hidden';
      this.wasAddToBagVisible = false;
    }

    return stateClass;
  }

  /**
   * Check if any selected item is out of stock
   * @returns {boolean} - True if any selected item is out of stock
   */
  hasOosItem() {
    return this.selectedItems.some((item) => item && !item.variant_available);
  }

  /**
   * Refresh the availability status of all selected items from current collection data
   * This ensures variant_available reflects the latest data from collections
   */
  refreshSelectedItemsAvailability() {
    this.selectedItems.forEach(item => {
      const collection = this.collections.find(c => c.id === item.of_collection_id);
      if (!collection) return;

      const product = collection.products.find(p => p.id === item.product_id);
      if (!product) return;

      const variant = product.variants.find(v => v.id === item.variant_id);
      if (variant) {
        item.variant_available = variant.available;
      }
    });

    this.applySelectedDuplicateInventoryCaps();
  }

  /**
   * Clean up stale OOS tile tracking - remove indices that are now filled
   */
  cleanupOosTileTracking() {
    this.removedOosTileIndices = this.removedOosTileIndices.filter(tileIndex => {
      const itemAtTile = this.selectedItems.find(item => item.tileIndex === tileIndex);
      return !itemAtTile; // Keep only if tile is still empty
    });
  }

  /**
   * Fetch live availability from Shopify for all selected items
   * Uses /products/{handle}.js with cache: 'no-cache' to get real-time stock data
   * @returns {Promise<Array>} - Array of OOS items (empty if all in stock)
   */
  async fetchLiveAvailability() {
    const uniqueHandles = [...new Set(
      this.selectedItems
        .filter(item => item && item.product_handle)
        .map(item => item.product_handle)
    )];

    if (uniqueHandles.length === 0) return [];

    const productsByHandle = {};

    await Promise.all(
      uniqueHandles.map(async (handle) => {
        const abortController = new AbortController();
        const timeoutId = setTimeout(() => abortController.abort(), 8000);

        try {
          const response = await fetch(`${window.Shopify.routes.root}products/${handle}.js?_=${Date.now()}`, {
            cache: 'no-store',
            signal: abortController.signal
          });
          if (response.ok) {
            productsByHandle[handle] = await response.json();
          }
        } catch (error) {
          console.error(`Error fetching live availability for "${handle}":`, error);
        } finally {
          clearTimeout(timeoutId);
        }
      })
    );

    // Update selectedItems with live availability
    this.selectedItems.forEach(item => {
      const liveProduct = productsByHandle[item.product_handle];
      if (!liveProduct) return;
      const liveVariant = liveProduct.variants.find(v => v.id === item.variant_id);
      if (liveVariant) {
        item.variant_available = liveVariant.available;
      }
    });

    this.applySelectedDuplicateInventoryCaps();

    return this.selectedItems.filter(item => item && !item.variant_available);
  }

  /**
   * Handle OOS items detected before cart add - shows modal
   * @param {Array} oosItems - Array of out-of-stock items
   */
  handleOosItemsBeforeAdd(oosItems) {
    if (!oosItems || oosItems.length === 0) return;
    this.showOosModal(oosItems);
  }

  /**
   * Show the OOS item modal with product names
   * @param {Array} oosItems - Array of out-of-stock items
   */
  showOosModal(oosItems) {
    const modal = document.querySelector('#OosItemModal');
    if (!modal) {
      // Fallback to alert if modal not found
      const oosProductNames = oosItems.map(item => item.product_title).join(', ');
      alert(`Sorry, "${oosProductNames}" is no longer available. Please select a different item.`);
      // Reload with OOS edit mode param (timestamp for cache busting)
      const url = new URL(window.location.href);
      url.searchParams.set('oos_edit', Date.now().toString());
      window.location.href = url.toString();
      return;
    }

    const productNamesEl = modal.querySelector('#OosProductNames');
    if (productNamesEl) {
      const oosProductNames = oosItems.map(item => item.product_title).join(', ');
      productNamesEl.textContent = oosProductNames;
    }

    // Set up the edit button handler
    const editBtn = modal.querySelector('#OosEditProductsBtn');
    if (editBtn) {
      editBtn.onclick = () => {
        modal.hide();
        // Add param so we enter OOS edit mode after reload (timestamp for cache busting)
        const url = new URL(window.location.href);
        url.searchParams.set('oos_edit', Date.now().toString());
        window.location.href = url.toString();
      };
    }

    modal.show();
  }

  /**
   * Check if we're in OOS edit mode (either has OOS item or had some auto-removed)
   * @returns {boolean} - True if in OOS edit mode
   */
  isInOosEditMode() {
    return this.editMode && (this.hasOosItem() || this.removedOosTileIndices.length > 0);
  }

  /**
   * Create HTML for the savings banner
   * @returns {string} - The savings banner HTML or empty string
   */
  createSavingsBannerHTML() {
    if (!this.settings?.show_savings_banner) {
      return '';
    }

    // Use previous savings if animation is pending
    const amount = this.pendingAnimation?.previousSavings ?? this.totalAmountSaved;
    let savingsMessage;

    const isStaticMode = this.isStaticSavingsMode();

    if (isStaticMode) {
      savingsMessage = this.settings?.savings_banner_message_override || this.strings?.start_saving || 'start saving!';
    } else if (amount > 0) {
      const roundedDown = Math.floor(amount / 100) * 100;
      const formattedSavings = this.formatSavingsCents(roundedDown);
      const defaultMessage = this.strings?.savings_message || '{{amount}} savings';
      savingsMessage = this.settings?.savings_banner_message_override
        ? this.settings.savings_banner_message_override.replace('{{amount}}', formattedSavings)
        : defaultMessage.replace('{{amount}}', formattedSavings);
    } else {
      savingsMessage = this.strings?.start_saving || 'start saving!';
    }

    return `<div class="custom-kit-savings-banner">${savingsMessage}</div>`;
  }

  /**
   * Create HTML for all header tiles (filled and empty)
   * @returns {string} - The tiles HTML
   */
  createHeaderTilesHTML() {
    const tilesHTML = [];
    let globalTileIndex = 0; // 0-based index matching currentHeaderTileIndex
    // Use previous tile index if animation is pending
    const activeTileIndex = this.pendingAnimation?.previousTileIndex ?? this.currentHeaderTileIndex;

    // Create tiles for each collection
    this.collections.forEach(collection => {
      const requiredSelections = collection.required_selections || 1;

      // Create tiles for this collection (filled and empty)
      for (let i = 0; i < requiredSelections; i++) {
        // Find item with this tileIndex
        const product = this.selectedItems.find(item => item.tileIndex === globalTileIndex);
        const tileNumber = globalTileIndex + 1; // Display number (1-based)
        const isActive = globalTileIndex === activeTileIndex;
        const activeClass = isActive ? ' active' : '';

        if (product) {
          // Filled tile - use variant image and product data
          const image = product.variant_featured_image || product.product_featured_image;
          const imageHTML = image && image.url ?
            `<img src="${image.url}"
                    alt="${image.alt || product.product_title}"
                    class="custom-kit-header-tile-image">` :
            '';

          // Check if variant is out of stock
          const isOutOfStock = !product.variant_available;
          const oosClass = isOutOfStock ? ' ck-item-out-of-stock' : '';

          tilesHTML.push(`
            <div class="custom-kit-header-tile-wrapper swiper-slide">
              <div class="custom-kit-header-tile filled ${activeClass}${oosClass}" data-product-id="${product.variant_id}" data-collection-id="${collection.id}" data-tile-index="${globalTileIndex}">
                ${imageHTML}
                <button class="custom-kit-header-tile-remove" data-product-id="${product.variant_id}" data-collection-id="${collection.id}" data-tile-index="${globalTileIndex}" aria-label="Remove ${product.product_title}"><span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17" aria-hidden="true"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/></svg></span></button>
              </div>
              <button class="custom-kit-header-tile-edit" data-product-id="${product.variant_id}" data-collection-id="${collection.id}" data-tile-index="${globalTileIndex}" type="button">edit</button>
            </div>
          `);
        } else {
          // Empty tile - check if this was an auto-removed OOS item
          const isRemovedOosTile = this.removedOosTileIndices.includes(globalTileIndex);
          const emptyOosClass = isRemovedOosTile ? ' ck-item-out-of-stock' : '';

          tilesHTML.push(`
            <div class="custom-kit-header-tile-wrapper swiper-slide">
              <div class="custom-kit-header-tile empty ${activeClass}${emptyOosClass}" data-collection-id="${collection.id}" data-tile-index="${globalTileIndex}">
                <div class="custom-kit-header-tile-placeholder">
                  <span class="custom-kit-header-tile-number">${tileNumber}</span>
                  <span class="custom-kit-header-tile-label">${collection.title}</span>
                </div>
                <svg class="custom-kit-header-tile-plus" width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 2V14M2 8H14" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
                </svg>
              </div>
            </div>
          `);
        }

        globalTileIndex++;
      }
    });

    this.headerTileCount = globalTileIndex;

    return tilesHTML.join('');
  }

  destroyHeaderSwiper() {
    if (this.headerSwiperResizeHandler) {
      window.removeEventListener('resize', this.headerSwiperResizeHandler);
      this.headerSwiperResizeHandler = null;
    }
    if (this.headerSwiperResizeTimeout) {
      clearTimeout(this.headerSwiperResizeTimeout);
      this.headerSwiperResizeTimeout = null;
    }
    if (this.headerSwiper) {
      this.headerSwiper.destroy(true, true);
      this.headerSwiper = null;
    }
    this.headerDom = null;
  }

  cacheHeaderDom() {
    const header = this.querySelector('.custom-kit-header');
    const tilesContainer = header?.querySelector('.custom-kit-header-tiles');
    const headerInfo = header?.querySelector('.custom-kit-header-info');
    const placeholders = header ? Array.from(header.querySelectorAll('.custom-kit-header-tile-placeholder')) : [];
    const plusIcons = header ? Array.from(header.querySelectorAll('.custom-kit-header-tile.empty .custom-kit-header-tile-plus')) : [];
    const navButtons = [];
    const { prevEl, nextEl } = this.headerSwiper?.navigation || {};
    if (prevEl) {
      if (Array.isArray(prevEl)) navButtons.push(...prevEl);
      else navButtons.push(prevEl);
    }
    if (nextEl) {
      if (Array.isArray(nextEl)) navButtons.push(...nextEl);
      else navButtons.push(nextEl);
    }

    this.headerDom = {
      header,
      tilesContainer,
      headerInfo,
      placeholders,
      plusIcons,
      navButtons
    };
  }

  initHeaderSwiper() {
    if (typeof Swiper === 'undefined') return;

    const swiperEl = this.querySelector('.custom-kit-header-tiles.swiper');
    if (!swiperEl) return;

    const prevEl = swiperEl.querySelector('.swiper-button-prev');
    const nextEl = swiperEl.querySelector('.swiper-button-next');
    const tileCount = this.headerTileCount || swiperEl.querySelectorAll('.swiper-slide').length;

    if (!prevEl || !nextEl || !tileCount) return;

    const initialSlide = Math.min(Math.max(this.currentHeaderTileIndex, 0), tileCount - 1);
    const isDesktop = window.innerWidth >= 750;
    const spaceBetween = isDesktop ? 24 : 16;

    // Calculate visible slides based on container and slide width
    const slide = swiperEl.querySelector('.swiper-slide');
    const visibleSlides = slide ? Math.floor((swiperEl.offsetWidth + spaceBetween) / (slide.offsetWidth + spaceBetween)) : 4;

    // On mobile, use the smaller of calculated visible or 4; on desktop use calculated
    const slidesPerView = isDesktop ? 'auto' : Math.min(4, tileCount);
    const effectiveVisible = isDesktop ? visibleSlides : Math.min(visibleSlides, typeof slidesPerView === 'number' ? slidesPerView : 4);

    // slidesPerGroup: visible slides - 1 (minimum 1)
    const slidesPerGroup = Math.max(1, Math.min(effectiveVisible - 1, tileCount - 1));

    this.headerSwiper = new Swiper(swiperEl, {
      slidesPerView,
      slidesPerGroup,
      spaceBetween,
      watchOverflow: true,
      initialSlide,
      navigation: { prevEl, nextEl },
      on: {
        afterInit: (swiper) => {
          this.updateHeaderSwiperNavigation(swiper);
          swiper.slideTo(initialSlide, 0, false);
          this.cacheHeaderDom();
        },
      },
    });

    // Set up debounced resize handler
    if (!this.headerSwiperResizeHandler) {
      this.headerSwiperResizeHandler = () => {
        clearTimeout(this.headerSwiperResizeTimeout);
        this.headerSwiperResizeTimeout = setTimeout(() => {
          this.headerSwiper?.destroy(true, true);
          this.headerSwiper = null;
          this.initHeaderSwiper();
          this.cacheHeaderDom();
        }, 150);
      };
      window.addEventListener('resize', this.headerSwiperResizeHandler);
    }
  }

  updateHeaderSwiperNavigation(swiper) {
    const container = swiper?.el;
    if (!container || !swiper?.navigation) return;

    const showNav = !swiper.isLocked;
    container.classList.toggle('has-nav', showNav);

    const { prevEl, nextEl } = swiper.navigation;
    const setDisplay = (el, value) => {
      if (Array.isArray(el)) el.forEach(e => e.style.display = value);
      else if (el) el.style.display = value;
    };

    setDisplay(prevEl, showNav ? 'flex' : 'none');
    setDisplay(nextEl, showNav ? 'flex' : 'none');
  }

  /**
   * Initialize scroll handler for sticky header shadow on desktop
   */
  initScrollHandler() {
    // Set header height CSS variable for collection header sticky positioning
    this.updateHeaderHeightVar();

    // Initial scroll state check
    this.handleScroll();

    // Set up scroll listener with requestAnimationFrame for performance
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });

    // Re-evaluate when crossing breakpoints and update header height
    window.addEventListener('resize', () => {
      this.updateHeaderHeightVar();
      this.handleScroll();
    });
  }

  /**
   * Update CSS variable with header height for collection header sticky positioning
   */
  updateHeaderHeightVar() {
    const header = this.querySelector('.custom-kit-header');
    if (header && window.innerWidth >= 750) {
      const headerHeight = header.offsetHeight;
      this.style.setProperty('--ck-header-height', `${headerHeight}px`);
    }
  }

  /**
   * Handle scroll event for sticky header behavior
   * Desktop: adds shadow when scrolled
   * Mobile: parallax animation with shrinking tiles and fading header info
   */
  handleScroll() {
    const header = this.headerDom?.header || this.querySelector('.custom-kit-header');
    if (!header) return;

    const scrollY = window.scrollY || 0;
    const isMobile = window.innerWidth < 750;

    const headerInfo = this.headerDom?.headerInfo || header.querySelector('.custom-kit-header-info');
    const tilesContainer = this.headerDom?.tilesContainer || header.querySelector('.custom-kit-header-tiles');

    if (!isMobile) {
      // Desktop: reset mobile styles and toggle scrolled class
      if (headerInfo) {
        headerInfo.style.opacity = '';
        headerInfo.style.maxHeight = '';
        headerInfo.style.marginBottom = '';
      }
      if (tilesContainer) {
        tilesContainer.style.removeProperty('--ck-tile-w');
        tilesContainer.style.removeProperty('--ck-tile-h');
        if (this.headerSwiper && this.headerSwiper.allowTouchMove === false) {
          this.headerSwiper.allowTouchMove = true;
          this.headerSwiper.enable?.();
        }
      }
      const placeholders = this.headerDom?.placeholders || header.querySelectorAll('.custom-kit-header-tile-placeholder');
      placeholders.forEach(el => {
        el.style.opacity = '';
      });
      const plusIcons = this.headerDom?.plusIcons || header.querySelectorAll('.custom-kit-header-tile-plus');
      plusIcons.forEach(el => {
        el.style.opacity = '';
      });
      const removeButtons = header.querySelectorAll('.custom-kit-header-tile-remove');
      removeButtons.forEach(el => {
        el.style.opacity = '';
        el.style.pointerEvents = '';
      });
      const editButtons = header.querySelectorAll('.custom-kit-header-tile-edit');
      editButtons.forEach(el => {
        el.style.opacity = '';
        el.style.marginTop = '';
      });

      // Desktop: collapse collection header on scroll
      const scrollThreshold = 100;
      const progress = Math.min(Math.max(scrollY / scrollThreshold, 0), 1);
      const easedProgress = progress * progress * (3 - 2 * progress);

      const collectionHeader = header.querySelector('.custom-kit-collection-header.active');
      if (collectionHeader) {
        const counter = collectionHeader.querySelector('.custom-kit-collection-item-counter');
        const title = collectionHeader.querySelector('.custom-kit-collection-title');
        const addToBagButton = header.querySelector('.custom-kit-add-to-bag');
        const isAddToBagVisible = addToBagButton && !addToBagButton.classList.contains('btn-hidden') && !addToBagButton.classList.contains('btn-hiding');

        // Fade out counter
        if (counter) {
          counter.style.opacity = 1 - easedProgress;
        }

        // Fade out title if add-to-bag button is visible, otherwise move it up
        if (title) {
          if (isAddToBagVisible) {
            title.style.opacity = 1 - easedProgress;
            title.style.transform = '';
          } else {
            title.style.opacity = '';
            title.style.transform = `translateY(${-easedProgress * 40}%)`;
          }
        }

        // Collapse the collection header by reducing margin/padding to move title up
        const fullHeight = collectionHeader.offsetHeight;
        collectionHeader.style.marginTop = `${-fullHeight * easedProgress}px`;
      }

      header.classList.toggle('scrolled', scrollY > 0);
      return;
    }

    // Mobile: parallax animation
    if (!headerInfo || !tilesContainer) return;

    const scrollThreshold = 100;
    const progress = Math.min(Math.max(scrollY / scrollThreshold, 0), 1);
    // Smoothstep easing for smoother animation
    const easedProgress = progress * progress * (3 - 2 * progress);

    // Device pixel ratio for crisp rendering
    const dpr = window.devicePixelRatio || 1;
    const snap = v => Math.round(v * dpr) / dpr;

    // Header info fade out and collapse
    headerInfo.style.opacity = 1 - easedProgress;
    headerInfo.style.maxHeight = `${snap(headerInfo.scrollHeight * (1 - easedProgress))}px`;
    headerInfo.style.marginBottom = easedProgress > 0.9 ? '0' : '';

    // Empty tile placeholders fade out, plus icon fades in (on active tile only)
    const placeholders = this.headerDom?.placeholders || header.querySelectorAll('.custom-kit-header-tile-placeholder');
    placeholders.forEach(el => {
      el.style.opacity = 1 - easedProgress;
    });
    // Use .active class to determine plus icon visibility (inherits animation delay from updateActiveTileDOM)
    const plusIcons = this.headerDom?.plusIcons || header.querySelectorAll('.custom-kit-header-tile.empty .custom-kit-header-tile-plus');
    plusIcons.forEach(el => {
      const parentTile = el.closest('.custom-kit-header-tile.empty');
      const isActive = parentTile?.classList.contains('active');
      if (isActive) {
        el.style.opacity = easedProgress;
      } else {
        el.style.opacity = 0;
      }
    });

    // Fade out remove and edit buttons on filled tiles
    const removeButtons = header.querySelectorAll('.custom-kit-header-tile-remove');
    removeButtons.forEach(el => {
      el.style.opacity = 1 - easedProgress;
      el.style.pointerEvents = progress >= 1 ? 'none' : 'auto';
    });
    const editButtons = header.querySelectorAll('.custom-kit-header-tile-edit');
    const editButtonCollapse = 18;
    editButtons.forEach(el => {
      el.style.opacity = 1 - easedProgress;
      el.style.marginTop = `${snap(-editButtonCollapse * easedProgress)}px`;
    });

    // Tile size interpolation (70px -> 40px width, maintaining aspect ratio)
    const maxTileWidth = 70;
    const minTileWidth = 40;
    const currentTileWidth = snap(maxTileWidth + (minTileWidth - maxTileWidth) * easedProgress);
    const currentTileHeight = snap(currentTileWidth * (93 / 70));

    tilesContainer.style.setProperty('--ck-tile-w', `${currentTileWidth}px`);
    tilesContainer.style.setProperty('--ck-tile-h', `${currentTileHeight}px`);

    const maxTileGap = 16;
    const minTileGap = 8;
    const currentTileGap = snap(maxTileGap + (minTileGap - maxTileGap) * easedProgress);
    if (this.headerSwiper) {
      const previousGap = this.headerSwiper.params?.spaceBetween;
      const gapChanged = previousGap === undefined ? true : Math.abs(previousGap - currentTileGap) > 0.5;
      if (gapChanged) {
        this.headerSwiper.params.spaceBetween = currentTileGap;
        this.headerSwiper.update();
        this.updateHeaderSwiperNavigation(this.headerSwiper);
      }
    }

    // Determine fit state based on current tile dimensions
    // Add 40px buffer (20px left padding + 20px right buffer) to ensure tiles aren't cut off at edge
    const tileCount = this.headerTileCount || 0;
    const currentTotalWidth = (tileCount * currentTileWidth) + ((tileCount - 1) * currentTileGap);
    const containerWidth = tilesContainer.offsetWidth || 0;
    const availableWidth = containerWidth - 40;
    const fitsInView = currentTotalWidth < availableWidth;

    // Hide nav arrows when all tiles fit in view at current size
    const navButtons = (this.headerDom?.navButtons && this.headerDom.navButtons.length)
      ? this.headerDom.navButtons
      : Array.from(header.querySelectorAll('.custom-kit-header-tiles .swiper-button-prev, .custom-kit-header-tiles .swiper-button-next'));
    if (navButtons.length) {
      const navOpacity = fitsInView ? 0 : 1;
      navButtons.forEach(btn => {
        btn.style.opacity = navOpacity;
        btn.style.pointerEvents = navOpacity === 0 ? 'none' : 'auto';
      });
    }

    // Disable swiper interaction when all tiles fit in view; re-enable when overflow returns
    if (this.headerSwiper) {
      if (fitsInView && this.headerSwiper.allowTouchMove !== false) {
        // Ensure we show the full set when locking; jump back to first slide if needed
        if (typeof this.headerSwiper.activeIndex === 'number' && this.headerSwiper.activeIndex !== 0) {
          this.headerSwiper.slideTo(0, 0, false);
        }
        this.headerSwiper.allowTouchMove = false;
        this.headerSwiper.disable?.();
      } else if (!fitsInView && this.headerSwiper.allowTouchMove === false) {
        this.headerSwiper.allowTouchMove = true;
        this.headerSwiper.enable?.();
      }
    }

    header.classList.toggle('scrolled', progress >= 1);
  }

  /**
   * Return the products that should appear in the grid for a collection
   * Respects availability, selections, display_oos flag, and the grid count limit
   * @param {Object} collection - The collection object
   * @returns {Array} - Filtered products for the grid
   */
  getGridProducts(collection) {
    if (!collection?.products) return [];

    const products = this.getInventoryAdjustedGridProducts(collection);
    const displayOosInGrid = this.settings?.display_oos_item_in_grid === true;
    let filteredProducts = products.filter(product => {
      const isSelected = this.hasSelectedByProductId(product.id, collection.id);
      // Show product if: available, already selected, or OOS display is enabled
      return product.available || isSelected || displayOosInGrid;
    });

    const maxProducts = collection.product_display_in_grid_count;
    if (maxProducts && maxProducts > 0) {
      const availableProducts = filteredProducts.filter(p => p.available);
      const selectedUnavailable = filteredProducts.filter(p =>
        !p.available && this.hasSelectedByProductId(p.id, collection.id)
      );
      filteredProducts = [
        ...availableProducts.slice(0, maxProducts),
        ...selectedUnavailable
      ];
    }

    return filteredProducts;
  }

  /**
   * Create HTML for all collections
   * @returns {string} - The collections HTML
   */
  createCollectionsHTML() {
    return this.collections.map((collection, index) => {
      const filteredProducts = this.getGridProducts(collection);

      const collectionHasVTOProducts = filteredProducts.some(p => p.vto_type);

      // Create an array to hold all items (products + content slots) with their positions
      const items = [];

      // First, add content slots with their preferred positions
      const contentSlotPositions = new Set();
      const vtoSlotPositions = new Set(); // Track VTO slots (full-width on mobile)
      if (collection.content_slots && collection.content_slots.length > 0) {
        collection.content_slots.forEach(contentSlot => {
          if (contentSlot.is_vto && !collectionHasVTOProducts) {
            return;
          }
          const position = parseInt(contentSlot.preferred_grid_position) || (filteredProducts.length + 1);
          contentSlotPositions.add(position);

          // Track VTO slots separately (full-width on mobile, 1 column on desktop)
          if (contentSlot.is_vto) {
            vtoSlotPositions.add(position);
          }

          items.push({
            type: 'content_slot',
            position: position,
            html: this.createContentSlotHTML(contentSlot)
          });
        });
      }

      // Add products, adjusting positions to account for content slots
      let productIndex = 0;
      filteredProducts.forEach((product) => {
        // Find the next available position (skip positions taken by content slots)
        let position = productIndex + 1;
        while (contentSlotPositions.has(position)) {
          position++;
        }

        // Eagerly load first 4 images of the first collection for better LCP
        const isAboveFold = index === 0 && productIndex < 4;

        items.push({
          type: 'product',
          position: position,
          html: this.createProductCardHTML(product, { isAboveFold })
        });

        productIndex++;
      });

      // Sort items by position and generate HTML
      items.sort((a, b) => a.position - b.position);
      const gridHTML = items.map(item => item.html).join('');

      // If there's a pending animation, keep the OLD collection active during render
      const activeIndex = this.pendingAnimation ? this.pendingAnimation.fromIndex : this.currentCollectionIndex;
      const activeClass = index === activeIndex ? ' active' : '';
      const loadingClass = (this.pendingAnimation && index === this.pendingAnimation.fromIndex) ? ' ck-loading' : '';
      const isComplete = this.getCollectionSelectionCount(collection.id) >= (collection.required_selections || 1);
      const collectionFullClass = isComplete ? ' collection-full' : '';
      const hidePricesGlobal = this.settings?.hide_grid_price ? ' prices-hidden--global' : '';
      const hidePricesCollection = collection.hide_collection_prices ? ' prices-hidden--collection' : '';

      return `
        <div class="custom-kit-collection-container${activeClass}${collectionFullClass}${loadingClass}${hidePricesGlobal}"
             data-collection-id="${collection.id}"
             data-collection-index="${index}">
          <div class="custom-kit-products-grid${hidePricesCollection}">
            ${gridHTML}
          </div>
        </div>
      `;
    }).join('');
  }

  /**
   * Get the formatted title HTML for a collection (e.g., "Choose 3 Lips products")
   * @param {Object} collection - The collection object
   * @returns {string} - The collection title HTML
   */
  getCollectionTitleHTML(collection) {
    const requiredSelections = collection.required_selections || 1;
    const productWord = requiredSelections === 1
      ? (this.strings?.product_singular || 'product')
      : (this.strings?.product_plural || 'products');
    const chooseText = this.strings?.choose || 'Choose';
    const title = `${chooseText} <strong>${requiredSelections}</strong> ${collection.title} ${productWord}`;
    return `<div class="custom-kit-collection-title">${title}</div>`;
  }

  /**
   * Create a product card HTML string
   * @param {Object} product - The product object
   * @param {Object} options - Rendering options
   * @param {boolean} options.isAboveFold - Whether this image is above the fold (for LCP optimization)
   * @returns {string} - The product card HTML
   */
  createProductCardHTML(product, options = {}) {
    const { isAboveFold = false } = options;

    let imageHTML = '';
    if (product.featured_image && product.featured_image.url) {
      const primaryImageUrl = product.featured_image.url_800 || product.featured_image.url;
      const srcset = [
        product.featured_image.url_200 ? `${product.featured_image.url_200} 200w` : '',
        product.featured_image.url_400 ? `${product.featured_image.url_400} 400w` : '',
        product.featured_image.url_600 ? `${product.featured_image.url_600} 600w` : '',
        product.featured_image.url_800 ? `${product.featured_image.url_800} 800w` : ''
      ].filter(Boolean).join(', ');

      // Above-fold images load eagerly with high priority for better LCP
      const loadingAttr = isAboveFold ? 'eager' : 'lazy';
      const fetchPriorityAttr = isAboveFold ? 'fetchpriority="high"' : '';

      imageHTML = `<img src="${primaryImageUrl}"
              srcset="${srcset}"
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
              alt="${product.featured_image.alt || product.title}"
              class="custom-kit-product-image"
              loading="${loadingAttr}"
              ${fetchPriorityAttr}
              onload="this.closest('.custom-kit-product-card').classList.remove('ck-img-loading')">`;
    }

    const iconPlus = `<div class="add-to-kit-plus-icon">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.5 2V7.5H14C14.25 7.5 14.5 7.75 14.5 8C14.5 8.28125 14.25 8.5 14 8.5H8.5V14C8.5 14.2812 8.25 14.5 8 14.5C7.71875 14.5 7.5 14.2812 7.5 14V8.5H2C1.71875 8.5 1.5 8.28125 1.5 8C1.5 7.75 1.71875 7.5 2 7.5H7.5V2C7.5 1.75 7.71875 1.5 8 1.5C8.25 1.5 8.5 1.75 8.5 2Z" fill="#4A444E"/>
        </svg>
      </div>`

    const iconCheckmark = `<div class="selected-item-checkmark-icon"><svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.5" y="0.5" width="39" height="39" rx="19.5" fill="#4A444E"/>
      <rect x="0.5" y="0.5" width="39" height="39" rx="19.5" stroke="#4A444E"/>
      <path d="M26.8438 15.1562C27.0312 15.3438 27.0312 15.6875 26.8438 15.875L18.3438 24.375C18.1562 24.5625 17.8125 24.5625 17.625 24.375L13.125 19.875C12.9375 19.6875 12.9375 19.3438 13.125 19.1562C13.3125 18.9688 13.6562 18.9688 13.8438 19.1562L18 23.3125L26.125 15.1562C26.3125 14.9688 26.6562 14.9688 26.8438 15.1562Z" fill="#F4F2F5"/>
      </svg></div>`

    const ratingHTML = this.getStarRatingHTML(product);
    const isSelected = this.hasSelectedByProductId(product.id, product.of_collection_id);
    const selectedClass = isSelected ? ' selected' : '';
    const isPercentageDiscount = this.isPercentagePriceDisplayMode();
    const originalPrice = isPercentageDiscount ? product.price : (product.compare_at_price || product.price);
    const originalPriceFormatted = isPercentageDiscount ? product.price_formatted : (product.compare_at_price_formatted || product.price_formatted);
    const showOriginalPrice = this.settings?.show_strikethrough_pricing !== false && originalPrice !== product.discount_price;

    // Check if product is OOS (non-shaded)
    const isOos = !product.available;
    const oosClass = isOos ? ' ck-product-oos' : '';
    const oosText = this.strings?.out_of_stock || 'out of stock';

    // Price or OOS text
    const priceOrOosHTML = isOos
      ? `<p class="custom-kit-oos-text">${oosText}</p>`
      : `${showOriginalPrice ? `<p class="custom-kit-original-price">${originalPriceFormatted}</p>` : ''}
            <p class="custom-kit-discount-price">${product.discount_price_formatted}</p>`;

    const imgLoadingClass = product.featured_image?.url ? ' ck-img-loading' : '';

    return `
      <div class="custom-kit-product-card${selectedClass}${oosClass}${imgLoadingClass}" data-product-id="${product.id}" data-collection-id="${product.of_collection_id}">
        <div class="custom-kit-product-image-wrapper">
          ${ratingHTML}
          ${imageHTML}
          ${isSelected ? iconCheckmark : iconPlus}
        </div>
        <div class="custom-kit-product-info">
          <h3 class="custom-kit-product-title">${product.title}</h3>
          <div class="custom-kit-product-price-container">
            ${priceOrOosHTML}
          </div>
        </div>
      </div>
    `;
  }

  getStarRatingHTML(product) {
    const average = Number(product.reviews_average);
    const count = Number(product.reviews_count);
    if (!this.shouldShowRating(average, count)) return '';

    const formattedCount = this.formatReviewCount(count);

    return `
      <div class="ratings-wrapper custom-kit__ratings-wrapper">
        <div class="star-rating-container">
          <div class="ratings-and-reviews">
            <div class="star-rating" data-review-average="${average}"></div>
            <div class="star-rating__amount">${formattedCount}</div>
          </div>
        </div>
      </div>
    `;
  }

  formatReviewCount(count) {
    const value = Number(count) || 0;
    if (value >= 1000) {
      const display = Math.floor(value / 100) / 10;
      const formatted = Number.isInteger(display) ? display.toFixed(0) : display.toFixed(1);
      return `${formatted}K`;
    }
    return value.toLocaleString();
  }

  shouldShowRating(avg, count) {
    const rating = Number(avg) || 0;
    const reviews = Number(count) || 0;
    if (reviews <= 0) return false;
    if (rating <= 2) return false;
    if (rating <= 3.5) return reviews >= 10;
    return true;
  }

  renderStarRatings() {
    const maxRating = 5;
    const stars = '★'.repeat(maxRating);

    this.querySelectorAll('.custom-kit-product-card .star-rating').forEach(el => {
      const rating = parseFloat(el.dataset.reviewAverage);
      if (isNaN(rating)) return;

      el.innerHTML = `
        <span class="star-bg">${stars}</span>
        <span class="star-fg">${stars}</span>
      `;

      const fg = el.querySelector('.star-fg');
      const clamped = Math.min(Math.max(rating, 0), maxRating);
      if (fg) {
        fg.style.width = `${(clamped / maxRating) * 100}%`;
      }

      el.dataset.rendered = 'true';
    });
  }

  /**
   * Create a content slot HTML card
   * @param {Object} contentSlot - The content slot object
   * @returns {string} - The HTML string for the content slot
   */
  createContentSlotHTML(contentSlot) {
    let tileInlineStyle = '';
    if (typeof contentSlot.media === 'string' && !contentSlot.text_with_overlay) {
      tileInlineStyle += `background-image: url(${contentSlot.media}); background-size: cover;`;
    }
    if (contentSlot.background_color) {
      tileInlineStyle += `background-color: ${contentSlot.background_color};`;
    }

    const shouldWrapTile = contentSlot.cta_text && !contentSlot.is_vto && contentSlot.cta_url;
    const ctaTarget = contentSlot.cta_new_tab ? '_blank' : '_self';
    const relAttr = contentSlot.cta_new_tab ? ' rel="noopener noreferrer"' : '';

    const rootUrl = document.querySelector('custom-kit')?.getAttribute('data-root-url') || '';
    let fullCtaUrl = '';
    if (contentSlot.cta_url) {
      if (contentSlot.cta_url === 'login=open') {
        fullCtaUrl = `${window.location.pathname}?login=open`;
      } else {
        const normalizedRoot = rootUrl.endsWith('/') ? rootUrl.slice(0, -1) : rootUrl;
        const normalizedPath = contentSlot.cta_url.startsWith('/') ? contentSlot.cta_url : `/${contentSlot.cta_url}`;
        fullCtaUrl = `${normalizedRoot}${normalizedPath}`;
      }
    }

    const hasOverlayImg = contentSlot.text_with_overlay && contentSlot.media;
    const imgLoadingClass = hasOverlayImg ? 'ck-img-loading' : '';

    const tileHTML = `
      <div class="custom-kit-content-slot ${contentSlot.is_vto ? `type--vto`: ``}
        marketing-tile ${contentSlot.vertical_alignment ? `vertical-alignment-${contentSlot.vertical_alignment}` : ''}
        ${contentSlot.text_with_overlay ? '' : 'full-tile-img'}
        ${contentSlot.overlay_position_4x1_variation == 'Right' ? 'xl-content-right' : 'xl-content-left'}
        ${imgLoadingClass}
        tile-cols-${contentSlot.size}" data-slot-name="${contentSlot.name || ''}" style="${tileInlineStyle}"
      >
        ${contentSlot.text_with_overlay && contentSlot.media ? `<img src="${contentSlot.media}" onload="this.closest('.custom-kit-content-slot').classList.remove('ck-img-loading')" />` : ''}
        <div class="ck-marketing-tile__details marketing-details ${contentSlot.horizontal_alignment ? contentSlot.horizontal_alignment : '' }">
          ${contentSlot.title ? `<p class="ck-marketing-tile__title title-font-${contentSlot.title_font ? contentSlot.title_font : `kanit`}" style="color:${contentSlot.title_color}">${contentSlot.title}</p>` : ''}
          ${contentSlot.short_description ? `<p class="ck-marketing-tile__desc marketing-desc desc-font-${contentSlot.description_font}" style="color:${contentSlot.description_color}">${contentSlot.short_description}</p>` : ''}
          ${shouldWrapTile ? `<span class="ck-marketing-tile__cta marketing-btn btn-${contentSlot.cta_style}" ${contentSlot.cta_text_color ? `style="color: ${contentSlot.cta_text_color}"` : ''}>${contentSlot.cta_text}</span>` : ''}
          ${contentSlot.cta_text && contentSlot.is_vto ?
            `<fetch-modal-opener class="find-my-shade__vto-modal-opener marketing-btn btn-button" data-url="/products/shape-tape-concealer?view=modal-vto" data-modal="#VtoModal" data-multi-product-vto="true"
              ${contentSlot.cta_text_color ? `style="--ck-vto-cta-color: ${contentSlot.cta_text_color}; border: 1px solid ${contentSlot.cta_text_color}"` : ''}>
              <button href="#" class="find-my-shade__option" style="color:${contentSlot.cta_text_color}">
                <span class="find-my-shade__option-virtual cta-font-${contentSlot.cta_font ? contentSlot.cta_font : `helvetica`}">
                  ${contentSlot.cta_text}
                </span>
              </button>
            </fetch-modal-opener>` : ''}
        </div>
      </div>
    `;

    if (!shouldWrapTile) {
      return tileHTML;
    }

    return `<a href="${fullCtaUrl}" target="${ctaTarget}"${relAttr} class="custom-kit-content-slot-link">${tileHTML}</a>`;
  }

  /**
   * Find a selected product by its id within a specific collection
   * @param {string|number} productId - The product ID to find
   * @param {string|number} collectionId - The collection ID to search within
   * @returns {Object|null} - The product object or null
   */
  findRawProductData(productId, collectionId) {
    const id = Number(productId);
    const colId = Number(collectionId);
    const collection = this.collections.find(c => c.id === colId);
    if (collection) {
      const product = collection.products.find(p => p.id === id);
      if (product) return product;
    }
    return null;
  }

  /**
   * Find a product and its collection by product ID
   * @param {string|number} productId - The product ID to locate
   * @returns {{product: Object|null, collection: Object|null}}
   */
  findProductAndCollectionByProductId(productId) {
    const id = Number(productId);
    for (const collection of this.collections) {
      const product = collection.products.find(p => Number(p.id) === id);
      if (product) {
        return { product, collection };
      }
    }
    return { product: null, collection: null };
  }

  /**
   * Find a selected item from selectedItems array by product ID and collection ID
   * @param {string|number} productId - The product ID to find
   * @param {string|number} collectionId - The collection ID to search within
   * @returns {Object|null} - The selected item object or null
   */
  findSelectedItemByProductId(productId, collectionId) {
    const id = Number(productId);
    const colId = Number(collectionId);
    return this.selectedItems.find(item =>
      item.product_id === id && item.of_collection_id === colId
    ) || null;
  }

  /**
   * Find a selected item from selectedItems array by variant ID and collection ID
   * @param {string|number} variantId - The variant ID to find
   * @param {string|number} collectionId - The collection ID to search within
   * @returns {Object|null} - The selected item object or null
   */
  findSelectedItemByVariantId(variantId, collectionId) {
    const id = Number(variantId);
    const colId = Number(collectionId);
    return this.selectedItems.find(item =>
      item.variant_id === id && item.of_collection_id === colId
    ) || null;
  }

  /**
   * Get count of selected items from a specific collection
   * @param {number|string} collectionId - The collection ID
   * @returns {number} - Count of selected items from this collection
   */
  getCollectionSelectionCount(collectionId) {
    const id = Number(collectionId);
    return this.selectedItems.filter(item => {
      return Number(item.of_collection_id) === id;
    }).length;
  }

  /**
   * Check if we can add another item to a collection
   * @param {number|string} collectionId - The collection ID to check
   * @returns {Object} - { canAdd: boolean, collection: Object, message: string }
   */
  canAddToCollection(collectionId) {
    const collection = this.collections.find(c => c.id === Number(collectionId));

    if (!collection) {
      return { canAdd: false, collection: null, message: 'Collection not found' };
    }

    const currentCount = this.getCollectionSelectionCount(collection.id);
    const requiredSelections = collection.required_selections || 1;

    if (currentCount >= requiredSelections) {
      return {
        canAdd: false,
        collection: collection,
        message: `You can only select ${requiredSelections} item(s) from "${collection.title}"`
      };
    }

    return { canAdd: true, collection: collection, message: '' };
  }

  /**
   * Update currentCollectionIndex and currentHeaderTileIndex to point to the first empty collection and tile
   * @returns {number} - The previous collection index (for animation scheduling)
   */
  updateCurrentIndicesToNextEmpty() {
    const previousCollectionIndex = this.currentCollectionIndex;
    let globalTileIndex = 0;

    // Iterate through each collection and its tiles
    for (let collectionIndex = 0; collectionIndex < this.collections.length; collectionIndex++) {
      const collection = this.collections[collectionIndex];
      const requiredSelections = collection.required_selections || 1;

      // Check each tile in this collection
      for (let i = 0; i < requiredSelections; i++) {
        const itemAtThisTile = this.selectedItems.find(item => item.tileIndex === globalTileIndex);

        if (!itemAtThisTile) {
          // Found the first empty tile
          this.currentHeaderTileIndex = globalTileIndex;
          this.currentCollectionIndex = collectionIndex;
          return previousCollectionIndex;
        }

        globalTileIndex++;
      }
    }

    // If all tiles are filled, set to last tile and last collection
    this.currentHeaderTileIndex = globalTileIndex;
    this.currentCollectionIndex = this.collections.length - 1;
    return previousCollectionIndex;
  }

  /**
   * Navigate to the next out-of-stock item when editing a kit
   * Should be called after items are loaded from URL params, and after each OOS item is replaced
   * For shaded items: scrolls to the product and opens quickview
   * For non-shaded items: removes the item from the selection (only on first call)
   * @returns {boolean} - True if navigated to an OOS tile, false if no OOS tiles need attention
   */
  navigateToFirstOosItemIfEditing() {
    // Only applies when in OOS edit mode (has OOS items or removed OOS tile indices)
    if (!this.editMode || !this.isInOosEditMode()) {
      return false;
    }

    // Find OOS items still in selectedItems (shaded items that need shade replacement)
    const oosItems = this.selectedItems
      .filter((item) => item && !item.variant_available)
      .sort((a, b) => a.tileIndex - b.tileIndex);

    // Categorize OOS items into shaded vs non-shaded and remove non-shaded ones
    for (const item of oosItems) {
      const product = this.findRawProductData(item.product_id, item.of_collection_id);
      const hasMultipleVariants = product && Array.isArray(product.variants) && product.variants.length > 1;

      // Remove non-shaded OOS items and track their tile indices (if not already tracked)
      if (!hasMultipleVariants && !this.removedOosTileIndices.includes(item.tileIndex)) {
        const tileIndex = item.tileIndex;
        this.removeItem(item.variant_id, item.of_collection_id);
        this.removedOosTileIndices.push(tileIndex);
      }
    }

    // Build list of all OOS tile positions that need attention
    // (shaded OOS items still in selectedItems + empty tiles from removed non-shaded OOS)
    const oosTileData = [];

    // Add shaded OOS items still in selectedItems
    for (const item of this.selectedItems) {
      if (!item.variant_available) {
        const product = this.findRawProductData(item.product_id, item.of_collection_id);
        oosTileData.push({
          tileIndex: item.tileIndex,
          isShaded: true,
          item,
          product
        });
      }
    }

    // Add empty tiles from removed non-shaded OOS items
    for (const tileIndex of this.removedOosTileIndices) {
      oosTileData.push({
        tileIndex,
        isShaded: false,
        item: null,
        product: null
      });
    }

    if (oosTileData.length === 0) {
      return false;
    }

    // Sort by tile index and get the first one
    oosTileData.sort((a, b) => a.tileIndex - b.tileIndex);
    const nextOos = oosTileData[0];

    // Navigate to this tile
    this.updateCurrentIndicesByHeaderTileIndex(nextOos.tileIndex);

    // Clear any pending animation so render() shows the correct collection
    this.pendingAnimation = null;

    // Set up scroll target and quickview for shaded items
    if (nextOos.item && nextOos.product) {
      this.pendingScrollTarget = {
        productId: nextOos.item.product_id,
        collectionId: nextOos.item.of_collection_id
      };

      if (nextOos.isShaded) {
        this.pendingQuickview = { product: nextOos.product, variantId: nextOos.item.variant_id, tileIndex: nextOos.item.tileIndex };
        this.prefetchPromise = fetch(nextOos.product.url).then(r => r.text()).catch(() => null);
      }
    }

    return true;
  }

  /**
   * Update currentCollectionIndex and currentHeaderTileIndex based on a clicked tile index
   * @param {number} tileIndex - The tile index (0-based) that was clicked
   * @returns {number} - The previous collection index (for animation scheduling)
   */
  updateCurrentIndicesByHeaderTileIndex(tileIndex) {
    const previousCollectionIndex = this.currentCollectionIndex;
    let globalTileIndex = 0;

    // Find which collection contains this tile
    for (let collectionIndex = 0; collectionIndex < this.collections.length; collectionIndex++) {
      const collection = this.collections[collectionIndex];
      const requiredSelections = collection.required_selections || 1;
      const tileRangeEnd = globalTileIndex + requiredSelections;

      // Check if the clicked tile falls within this collection's range
      if (tileIndex >= globalTileIndex && tileIndex < tileRangeEnd) {
        this.currentHeaderTileIndex = tileIndex;
        this.currentCollectionIndex = collectionIndex;
        return previousCollectionIndex;
      }

      globalTileIndex = tileRangeEnd;
    }
    return previousCollectionIndex;
  }

  /**
   * Build item object with both product and variant data
   * @param {Object} product - The product object
   * @param {Object} variant - The variant object
   * @param {number} tileIndex - The global tile index (0-based) for this item
   * @returns {Object} - Item object ready to be added to selectedItems
   */
  buildSelectedItemObject(product, variant, tileIndex) {
    const categoryIndex = this.getItemCategoryIndex(product, tileIndex);
    const discountPrice = this.getItemDiscountPrice(product, variant, tileIndex);

    return {
      variant_id: variant.id,
      variant_title: variant.title,
      variant_price: variant.price,
      variant_compare_at_price: variant.compare_at_price,
      variant_available: variant.available,
      variant_featured_image: variant.featured_image || product.featured_image,
      product_id: product.id,
      product_handle: product.handle,
      product_title: product.title,
      product_url: product.url,
      product_price: product.price,
      product_discount_price: discountPrice,
      product_compare_at_price: variant.compare_at_price || product.compare_at_price,
      amount_saved: this.getItemAmountSaved(product, variant, tileIndex),
      product_available: product.available,
      product_featured_image: product.featured_image,
      reviews_average: product.reviews_average,
      reviews_count: product.reviews_count,
      of_collection_id: product.of_collection_id,
      of_collection_handle: product.of_collection_handle,
      category_index: categoryIndex,
      tileIndex: tileIndex
    };
  }

  /**
   * Calculate the amount saved for a single product
   * @param {Object} product - The product object
   * @param {Object} variant - The variant object (optional)
   * @returns {number} - The amount saved in cents (never negative)
   */
  getItemAmountSaved(product, variant, tileIndex) {
    if (this.isStaticSavingsMode()) return 0;

    if (this.isBuyOneGetOneDiscount()) {
      const categoryIndex = this.getItemCategoryIndex(product, tileIndex);
      if (categoryIndex === 0) return 0;

      const originalPrice = variant?.price || product.price || 0;
      const discountPrice = this.getBogoDiscountedPriceCents(originalPrice, this.getCurrentCountry(), categoryIndex);
      return Math.max(0, originalPrice - discountPrice);
    }

    // Use variant compare_at_price if available, otherwise fall back to product
    const originalPrice = variant?.compare_at_price || product.compare_at_price || product.price || 0;
    const discountPrice = product.discount_price || product.price || 0;
    return Math.max(0, originalPrice - discountPrice);
  }

  /**
   * Handle product card click based on element clicked and product state
   * @param {Object} product - The product object
   * @param {string} clickedElement - 'image', 'title', or 'price'
   */
  handleProductCardClick(product, clickedElement) {
    const selectedItem = this.findSelectedItemByProductId(product.id, product.of_collection_id);
    const isSelected = !!selectedItem;
    const hasMultipleVariants = product.variants && product.variants.length > 1;
    const { canAdd } = this.canAddToCollection(product.of_collection_id);

    if (hasMultipleVariants) {
      if (isSelected && !canAdd) {
        if (clickedElement === 'image') {
          this.removeItemByTileIndex(selectedItem.tileIndex);
          this.render();
          return;
        }

        // If clicking a selected product anywhere on the card (except the checkmark handled earlier), open edit mode.
        if (clickedElement === 'title' || clickedElement === 'price') {
          this.openCustomKitQuickShop(product, true, selectedItem.variant_id, selectedItem.tileIndex);
          return;
        }
      }

      // When there is room in the collection, opening add mode lets shoppers select the same variant again.
      this.openCustomKitQuickShop(product);
      return;
    }

    // For single variant products, toggle directly
    this.toggleProduct(product);
  }

  /**
   * Toggle product selection (add or remove)
   * Assumes product has only one variant or the desired variant is the first one
   * @param {Object} product - The product object
   */
  toggleProduct(product) {
    // Get the variant (assumes single variant product)
    const variant = product.variants && product.variants.length > 0
      ? product.variants[0]
      : null;

    if (!variant) {
      console.error('No variant found for product:', product);
      return;
    }

    const variantId = variant.id;
    const selectedItem = this.findSelectedItemByVariantId(variantId, product.of_collection_id);

    const { canAdd, message } = this.canAddToCollection(product.of_collection_id);

    if (selectedItem) {
      this.removeItemByTileIndex(selectedItem.tileIndex);
      this.render();
      return;
    }

    if (!canAdd) {
      console.warn(message);
      alert(message);
      return;
    } else {
      if (!this.canAddVariantWithinInventory(product, variant)) {
        return;
      }

      // Add item to kit
      this.addItem(product, variant);
    }

    // Re-render to update header and card states
    this.render();
  }

  /**
   * Handle "ADD KIT TO BAG" button click
   */
  async handleAddCustomKitToBag() {
    // Validate that all required selections are made
    if (this.editMode && (!this.allRequirementsMet || !this.itemWasUpdated)) {
      await this.openCart();
      return;
    }

    if (!this.allRequirementsMet) {
      const errorMessage = this.strings.no_items_selected || 'Please select all required items';
      alert(errorMessage);
      console.warn('Missing required selections');
      return;
    }

    // Check if we should add products normally or as a bundle
    if (this.settings.add_to_cart_as_custom_kit) {
      await this.addAsBundle();
    } else {
      await this.addAllProductsNormally();
    }
  }

  /**
   * Add selected items as a bundle with bundle properties
   */
  async addAsBundle() {
    // Check if max custom kits reached (only for new kits, not edits)
    if (!this.editMode) {
      const isMaxReached = await this.isCustomKitMaxReached(true);
      if (isMaxReached) {
        return;
      }
    }

    const button = this.querySelector('.custom-kit-add-to-bag');
    button.disabled = true;

    await this.refreshCartVariantQuantities({ forceFetch: true });

    // Fetch LIVE availability from Shopify before attempting cart add (silent check)
    let oosItems;
    try {
      oosItems = await this.fetchLiveAvailability();
    } catch (error) {
      console.error('Error checking live availability:', error);
      // Fall back to stale data check if live fetch fails
      this.refreshSelectedItemsAvailability();
      oosItems = this.selectedItems.filter(item => item && !item.variant_available);
    }

    this.cleanupOosTileTracking();

    // If any items are OOS, abort and navigate user to fix
    if (oosItems.length > 0) {
      button.disabled = false;
      button.textContent = this.getAddToBagText();
      this.handleOosItemsBeforeAdd(oosItems);
      return;
    }

    // Prepare bundle metadata from customKitProduct (now variant-based)
    const bundleKey = `custom_kit_${new Date().getTime()}`;

    // Create custom kit URL with bundle key
    const customKitUrlObj = new URL(this.currentCustomKitUrl || window.location.href);
    customKitUrlObj.searchParams.set('bundle_key', bundleKey);
    const customKitUrlWithKey = customKitUrlObj.toString();

    const bundleMetadata = {
      bundleKey: bundleKey,
      parentId: this.customKitProduct?.variant_id || '',
      bundleSku: this.customKitProduct?.variant_sku || '',
      bundleName: this.customKitProduct?.product_title || 'Custom Kit',
      bundleImage: this.customKitProduct?.product_featured_image?.url || '',
      bundleProductId: this.customKitProduct?.product_id || '',
      bundleHandle: this.customKitProduct?.product_handle || '',
      bundleShade: this.customKitProduct?.variant_shade || '',
      bundleSize: '',
      promoExclusions: this.customKitProduct?.promo_exclusions ? 'true' : 'false',
      customKitUrl: customKitUrlWithKey,
      customKitTotalSavings: this.totalAmountSaved.toString(),
      isFinalSale: this.customKitProduct?.is_final_sale ? 'true' : 'false'
    };

    try {
      button.textContent = this.editMode ? this.strings.updating || 'Updating...' : this.strings.adding || 'Adding...';
      const cartResponse = await this.addBundleToCart(null, bundleMetadata);

      // Show success message
      const successMessage = this.strings.added_to_cart || 'Added to cart successfully';
      console.log(successMessage);

      // Trigger cart drawer if it exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        // Fetch fresh sections after cart/add.js so Shopify's cart transform has
        // populated item_components for every bundle/custom kit in the drawer.
        const sections = await this.fetchCartSections(['cart-drawer', 'cart-icon-bubble']);
        cartDrawer.renderContents({ ...cartResponse, sections });
      }

      window.history.replaceState({}, '', window.location.origin + window.location.pathname);
      this.clearItems();
      this.editMode = false;
      this.itemWasUpdated = false;
      this.render();
    } catch (error) {
      console.error('Error adding kit to cart:', error);

      // Restore button state so user can retry
      button.disabled = false;
      button.textContent = this.getAddToBagText();
    }
  }

  /**
   * Fetch cart sections (similar to product-form.js)
   * @param {Array} sections - Array of section IDs to fetch
   * @returns {Promise} - Resolves with sections object
   */
  async fetchCartSections(sections = ['cart-drawer', 'cart-icon-bubble']) {
    try {
      const root = window.Shopify.routes?.root || '';
      const localePrefix = root.endsWith('/') ? root.slice(0, -1) : root;
      const params = new URLSearchParams();
      params.append('sections', sections.join(','));
      const response = await fetch(`${localePrefix}/?${params.toString()}`, {
        cache: 'no-store',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      if (!response.ok) throw new Error(`Sections request failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart sections:', error);
      return {};
    }
  }

  publishCartUpdate(eventData) {
    if (typeof publishDeferred === 'function') {
      publishDeferred(PUB_SUB_EVENTS.cartUpdate, eventData);
      return;
    }

    window.setTimeout(() => publish(PUB_SUB_EVENTS.cartUpdate, eventData), 0);
  }

  /**
   * Add all selected items to cart normally (without bundle properties)
   */
  async addAllProductsNormally() {
    const button = this.querySelector('.custom-kit-add-to-bag');
    const addToBagText = this.strings.add_to_bag || 'ADD KIT TO BAG';

    button.disabled = true;

    await this.refreshCartVariantQuantities({ forceFetch: true });

    // Fetch LIVE availability from Shopify before attempting cart add (silent check)
    let oosItems;
    try {
      oosItems = await this.fetchLiveAvailability();
    } catch (error) {
      console.error('Error checking live availability:', error);
      this.refreshSelectedItemsAvailability();
      oosItems = this.selectedItems.filter(item => item && !item.variant_available);
    }

    this.cleanupOosTileTracking();

    // If any items are OOS, abort and navigate user to fix
    if (oosItems.length > 0) {
      button.disabled = false;
      button.textContent = addToBagText;
      this.handleOosItemsBeforeAdd(oosItems);
      return;
    }

    try {
      button.textContent = this.editMode ? this.strings.updating || 'Updating...' : this.strings.adding || 'Adding...';

      const variantIds = this.getVariantIds();
      const cartDrawer = document.querySelector('cart-drawer');
      const sectionIds = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map(section => section.id)
        : ['cart-drawer', 'cart-icon-bubble'];
      const itemsByVariantId = variantIds.reduce((items, variantId) => {
        const id = Number(variantId);
        if (!Number.isFinite(id)) return items;
        items.set(id, (items.get(id) || 0) + 1);
        return items;
      }, new Map());

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: Array.from(itemsByVariantId.entries()).map(([id, quantity]) => ({ id, quantity })),
          sections: sectionIds,
          sections_url: window.location.pathname
        })
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Error adding to cart');
      }

      // Show success message
      const successMessage = this.strings.added_to_cart || 'Added to cart successfully';
      console.log(successMessage);

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        if (!data.sections) {
          data.sections = await this.fetchCartSections(sectionIds);
        }
        cartDrawer.renderContents(data);
      }

      // Sync full cart data after the UI render path instead of blocking the drawer open.
      this.publishCartUpdate({
        source: 'custom-kit-normal',
        cartData: data,
      });

      window.history.replaceState({}, '', window.location.origin + window.location.pathname);
      this.clearItems();
      this.editMode = false;
      this.itemWasUpdated = false;
      this.render();

    } catch (error) {
      const errorMessage = this.strings.error_adding_to_cart || 'Error adding to cart';
      alert(errorMessage + ': ' + error.message);
      console.error('Error adding kit to cart:', error);
    } finally {
      button.disabled = false;
      button.textContent = addToBagText;
    }
  }

  /**
   * Add an item (variant) to the items array
   * @param {Object} product - The product object
   * @param {Object} variant - The variant object
   * @returns {boolean} - True if added, false if already exists
   */
  addItem(product, variant) {
    if (!product || !variant) {
      console.error('Invalid product or variant:', product, variant);
      return false;
    }

    if (!this.canAddVariantWithinInventory(product, variant)) {
      console.warn('Variant is not available for this kit selection:', variant.id);
      return false;
    }

    // Find the appropriate tile index - use current if empty, otherwise find next empty in same collection
    const tileIndex = this.getNextEmptyTileInCollection(product.of_collection_id);
    if (tileIndex === null) {
      console.warn('No empty tiles available in collection:', product.of_collection_id);
      return false;
    }

    const itemToAdd = this.buildSelectedItemObject(product, variant, tileIndex);
    this.selectedItems.push(itemToAdd);

    // Clear OOS edit state if filling a tile that had a removed OOS item
    const oosIndex = this.removedOosTileIndices.indexOf(tileIndex);
    if (oosIndex !== -1) {
      this.removedOosTileIndices.splice(oosIndex, 1);
    }

    this.onItemsChanged();
    console.log('Added item to kit:', product.title, 'at tile index', tileIndex, 'Total items:', this.selectedItems.length);
    return true;
  }

  /**
   * Get the next empty tile index within a specific collection
   * @param {number|string} collectionId - The collection ID
   * @returns {number|null} - The tile index or null if no empty tiles
   */
  getNextEmptyTileInCollection(collectionId) {
    const colId = Number(collectionId);
    let globalTileIndex = 0;

    for (let i = 0; i < this.collections.length; i++) {
      const collection = this.collections[i];
      const requiredSelections = collection.required_selections || 1;

      if (collection.id === colId) {
        // Found the collection, now find the first empty tile within it
        for (let j = 0; j < requiredSelections; j++) {
          const tileIdx = globalTileIndex + j;
          const itemAtTile = this.selectedItems.find(item => item.tileIndex === tileIdx);
          if (!itemAtTile) {
            return tileIdx;
          }
        }
        // No empty tiles in this collection
        return null;
      }

      globalTileIndex += requiredSelections;
    }

    return null;
  }

  /**
   * Remove a product from the items array by variant ID and collection ID
   * @param {number|string} variantId - The variant ID to remove
   * @param {number|string} collectionId - The collection ID to remove from
   * @returns {boolean} - True if removed, false if not found
   */
  removeItem(variantId, collectionId) {
    const selectedItem = this.findSelectedItemByVariantId(variantId, collectionId);

    if (!selectedItem) {
      console.warn('Item not found in kit:', variantId, 'in collection:', collectionId);
      return false;
    }

    this.lastActionTileIndex = selectedItem.tileIndex;
    const index = this.selectedItems.indexOf(selectedItem);
    const removed = this.selectedItems.splice(index, 1)[0];

    this.onItemsChanged();
    return true;
  }

  removeItemByTileIndex(tileIndex) {
    const selectedItem = this.selectedItems.find(item => item.tileIndex === Number(tileIndex));

    if (!selectedItem) {
      console.warn('Item not found in kit at tile:', tileIndex);
      return false;
    }

    this.lastActionTileIndex = selectedItem.tileIndex;
    const index = this.selectedItems.indexOf(selectedItem);
    this.selectedItems.splice(index, 1);

    this.onItemsChanged();
    return true;
  }

  /**
   * Clear all items from the kit
   */
  clearItems() {
    this.selectedItems = [];
    this.onItemsChanged();
  }

  /**
   * Get all items in the kit
   * @returns {Array} - Array of product objects
   */
  getItems() {
    return [...this.selectedItems];
  }

  /**
   * Get a specific item by variant ID
   * @param {number|string} variantId - The variant ID to find
   * @returns {Object|null} - The product object or null if not found
   */
  getItem(variantId) {
    const id = Number(variantId);
    return this.selectedItems.find(item => item.variant_id === id) || null;
  }

  /**
   * Check if a variant ID is in the kit within a specific collection
   * @param {number|string} variantId - The variant ID to check
   * @param {number|string} collectionId - The collection ID to check
   * @returns {boolean} - True if in kit, false otherwise
   */
  hasSelectedByVariantId(variantId, collectionId) {
    const id = Number(variantId);
    const colId = Number(collectionId);
    return this.selectedItems.some(item =>
      item.variant_id === id && item.of_collection_id === colId
    );
  }

  /**
   * Check if a product is in the kit by product ID and collection ID
   * @param {number|string} productId - The product ID to check
   * @param {number|string} collectionId - The collection ID to check
   * @returns {boolean} - True if in kit, false otherwise
   */
  hasSelectedByProductId(productId, collectionId) {
    const id = Number(productId);
    const colId = Number(collectionId);
    return this.selectedItems.some(item =>
      item.product_id === id && item.of_collection_id === colId
    );
  }

  /**
   * Calculate availability data for all collections from already loaded collection data
   * Stores result in this.collectionAvailability
   */
  calculateCollectionAvailability() {
    this.collectionAvailability = this.collections.map(collection => {
      const products = this.getInventoryAdjustedGridProducts(collection);
      const totalProducts = products.length;
      const inStockCount = products.filter(p => p.available).length || 0;
      const outOfStockCount = totalProducts - inStockCount;

      return {
        collection_id: collection.id,
        collection_handle: collection.handle,
        total_products: totalProducts,
        in_stock_count: inStockCount,
        out_of_stock_count: outOfStockCount,
        all_out_of_stock: inStockCount === 0
      };
    });
  }

  /**
   * Handle the sold-out state for custom kit.
   * Evaluates `collectionAvailability` and toggles a "sold out" state class
   * on the custom kit container. Actual visibility changes are
   * delegated to CSS.
   *
   * @returns {boolean} - `true` if any collection is fully out of stock and
   *                      the page is marked as sold out; otherwise `false`.
   */
  applySoldOutState() {
    const isSoldOut = this.collectionAvailability?.some(i => i.all_out_of_stock) ?? false;
    const container = this.closest('.custom-kit-section');

    if (!container) return;

    container.classList.toggle('custom-kit--sold-out', Boolean(isSoldOut));

    if (isSoldOut) {
      const url = new URL(window.location.href);
      if (url.searchParams.has('variants')) {
        url.searchParams.delete('variants');
        window.history.replaceState({}, '', url.toString());
      }
    }

    return Boolean(isSoldOut);
  }

  /**
   * Get availability data for a specific collection
   * @param {number|string} collectionId - The collection ID
   * @returns {Object|null} - Availability data or null if not found
   */
  getCollectionAvailability(collectionId) {
    const id = Number(collectionId);
    return this.collectionAvailability.find(a => a.collection_id === id) || null;
  }

  /**
   * Get the count of items in the kit
   * @returns {number} - Number of items
   */
  getItemCount() {
    return this.selectedItems.length;
  }

  /**
   * Get an array of just the variant IDs
   * @returns {Array} - Array of variant IDs
   */
  getVariantIds() {
    return this.selectedItems.map(item => item.variant_id);
  }

  getBundleComponents(variantIds = this.getVariantIds(), componentPricingBySlot = {}) {
    if (this.selectedItems.length) {
      return this.selectedItems
        .slice()
        .sort((a, b) => a.tileIndex - b.tileIndex)
        .map((item) => {
          const slot = String(item.tileIndex + 1);
          const customKitPricing = componentPricingBySlot[slot];

          return {
            merchandiseId: window.toShopifyVariantGid(item.variant_id),
            variantId: String(item.variant_id),
            quantity: 1,
            slot,
            ...(customKitPricing ? { customKitPricing } : {})
          };
        });
    }

    return variantIds.map((variantId, index) => ({
      merchandiseId: window.toShopifyVariantGid(variantId),
      variantId: String(variantId),
      quantity: 1,
      slot: String(index + 1)
    }));
  }

  /**
   * Calculate total amount saved across all selected items
   * @returns {number} - Total savings in cents
   */
  calculateTotalAmountSaved() {
    if (this.isStaticSavingsMode()) {
      this.totalAmountSaved = 0;
      return this.totalAmountSaved;
    }

    this.totalAmountSaved = this.selectedItems.reduce((total, item) => {
      return total + (item.amount_saved || 0);
    }, 0);
    return this.totalAmountSaved;
  }

  /**
   * Determine if savings banner should be copy-only with no calculations
   * @returns {boolean}
   */
  isStaticSavingsMode() {
    return this.settings?.discount_type === 'none';
  }

  /**
   * Get total amount saved formatted as currency (rounded down to nearest dollar, no decimals)
   * @returns {string} - Formatted total savings (e.g., "$29" not "$29.00")
   */
  getTotalAmountSavedFormatted() {
    // Round down to nearest dollar (e.g., 2999 cents -> 2900 cents -> $29)
    const roundedDown = Math.floor(this.totalAmountSaved / 100) * 100;
    return this.formatSavingsCents(roundedDown);
  }

  /**
   * Called whenever items array changes - override this for custom behavior
   */
  onItemsChanged() {
    // Store previous values before updating (for delayed animation updates)
    const previousCollectionIndex = this.currentCollectionIndex;
    const previousTileIndex = this.currentHeaderTileIndex;
    this.previousHeaderTileIndex = previousTileIndex;

    // If a specific tile was acted on (e.g., removal), focus there; otherwise advance to next tile
    if (this.lastActionTileIndex !== null && this.lastActionTileIndex !== undefined) {
      this.updateCurrentIndicesByHeaderTileIndex(this.lastActionTileIndex);
    } else if (this.isInOosEditMode()) {
      // In OOS edit mode, navigate to the next OOS tile that needs attention
      this.navigateToFirstOosItemIfEditing();
    } else {
      this.updateCurrentIndicesToNextEmpty();
    }

    // Trigger grid animation if collection changed
    this.scheduleAnimationIfChanged(previousCollectionIndex, previousTileIndex, { allowScrollToTop: true });

    const tileAdvanced = this.currentHeaderTileIndex !== previousTileIndex;
    const hasPendingAnimation = !!this.pendingAnimation;
    if (tileAdvanced && !hasPendingAnimation) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    this.lastActionTileIndex = null;

    // Calculate total savings
    this.calculateTotalAmountSaved();

    // Update URL query params with selected variant IDs
    this.updateURLParams();

    // Update the allRequirementsMet flag
    this.updateAllRequirementsMet();

    // Update the flag
    this.itemWasUpdated = true;

    // Dispatch custom event for external listeners
    this.dispatchEvent(new CustomEvent('items-changed', {
      detail: {
        items: this.getItems(),
        count: this.getItemCount(),
        totalAmountSaved: this.totalAmountSaved
      },
      bubbles: true
    }));
  }

  /**
   * Load selected items from URL query parameters
   * Format: variants=variantId:collectionId:tileIndex,variantId:collectionId:tileIndex,...
   */
  loadFromURLParams() {
    const url = new URL(window.location.href);
    const urlVariantsParam = url.searchParams.get('variants');
    const savedVariantsParam = sessionStorage.getItem(this.SS_KEY);
    const variantsParam = urlVariantsParam || savedVariantsParam;
    const restoredFromSession = !urlVariantsParam && !!savedVariantsParam;

    if (!variantsParam) {
      return;
    }

    // Parse variant_id:collection_id:tile_index triplets
    const variantTriplets = variantsParam.split(',').map(triplet => {
      const [variantId, collectionId, tileIndex] = triplet.split(':').map(id => Number(id.trim()));
      return { variantId, collectionId, tileIndex };
    }).filter(triplet => !isNaN(triplet.variantId) && !isNaN(triplet.collectionId) && !isNaN(triplet.tileIndex));

    // Find and add each variant to selectedItems
    variantTriplets.forEach(({ variantId, collectionId, tileIndex }) => {
      // Find the collection that contains this item
      const collection = this.collections.find(c => c.id === collectionId);

      if (collection) {
        for (const product of collection.products) {
          const variant = product.variants.find(v => v.id === variantId);

          if (variant) {
            // Add without calling onItemsChanged (which would update URL again)
            const tileIsEmpty = !this.selectedItems.some(item => item.tileIndex === tileIndex);
            if (tileIsEmpty) {
              const itemToAdd = this.buildSelectedItemObject(product, variant, tileIndex);
              this.selectedItems.push(itemToAdd);
            }
            return; // Found the variant, move to next triplet
          }
        }
      }
    });

    // If restored from sessionStorage (not URL), sync URL to reflect loaded state
    if (restoredFromSession && this.selectedItems.length > 0) {
      this.updateURLParams();
    }

    console.log('Loaded items from URL:', this.selectedItems.length);
  }

  /**
   * Update URL query parameters with selected variant IDs
   */
  updateURLParams() {
    const url = new URL(window.location.href);

    if (this.selectedItems.length > 0) {
      // Store variant_id:collection_id:tile_index triplets
      const params = this.selectedItems.map(item =>
        `${item.variant_id}:${item.of_collection_id}:${item.tileIndex}`
      ).join(',');
      url.searchParams.set('variants', params);
      sessionStorage.setItem(this.SS_KEY, params);
    } else {
      url.searchParams.delete('variants');
      sessionStorage.removeItem(this.SS_KEY);
    }

    // Store the URL for later use
    this.currentCustomKitUrl = url.toString();

    // Update URL without reloading the page
    window.history.replaceState({}, '', this.currentCustomKitUrl);
  }

  /**
   * Check if all required selections are met across all collections
   * @returns {boolean} - True if all requirements met, false otherwise
   */
  updateAllRequirementsMet() {
    for (const collection of this.collections) {
      const requiredSelections = collection.required_selections || 1;
      const currentCount = this.getCollectionSelectionCount(collection.id);

      if (currentCount < requiredSelections) {
        this.allRequirementsMet = false;
        return;
      }
    }
    this.allRequirementsMet = true;
  }

  /**
   * Add items to cart as a bundle (similar to product-form.js bundle logic)
   * @param {Array} items - Array of variant IDs to add as a bundle (optional, defaults to this.items variant IDs)
   * @param {Object} bundleMetadata - Metadata for the bundle (key, name, image, etc.)
   * @returns {Promise} - Resolves with cart response
   */
  async addBundleToCart(items = null, bundleMetadata = {}) {
    const variantIds = items || this.getVariantIds();

    if (!variantIds.length) {
      throw new Error('No items to add to cart');
    }
    const {
      bundleKey = '',
        parentId = '',
        bundleSku = '',
        bundleName = '',
        bundleImage = '',
        bundleProductId = '',
        bundleHandle = '',
        bundleShade = '',
        bundleSize = '',
        promoExclusions = '',
        customKitUrl = '',
        customKitTotalSavings = '',
        isFinalSale,
        ...additionalProps
    } = bundleMetadata;

    const props = {
      '_bundle_key': bundleKey,
      '_parent_id': parentId,
      '_bundle_sku': bundleSku,
      '_bundle_name': bundleName,
      '_bundle_image': bundleImage,
      '_bundle_product_id': bundleProductId,
      '_bundle_handle': bundleHandle,
      '_bundle_shade': bundleShade,
      '_bundle_size': bundleSize,
      '_promo_exclusions': promoExclusions,
      '_custom_kit_url': customKitUrl,
      '_custom_kit_total_savings': customKitTotalSavings,
      '_is_custom_kit': 'true',
      '_is_final_sale': isFinalSale,
      ...additionalProps
    };

    // Store original locale for currency conversion
    props['_ck_original_locale'] = window?.Shopify?.country || 'US';

    if (this.settings?.discount_type) {
      props['_discount_type'] = this.settings.discount_type;
    }

    if (this.settings?.discount_percentage !== undefined && this.settings?.discount_percentage !== null) {
      props['_discount_percentage'] = String(this.settings.discount_percentage);
    }

    // Add countdown timer properties if enabled
    if (this.settings?.enable_bag_countdown_timer) {
      props['_countdown_timer_time_amount'] = this.settings?.countdown_timer_time_amount;
      if (this.strings?.countdown_timer_copy) {
        props['_countdown_timer_copy'] = this.strings.countdown_timer_copy;
      }
      if (this.settings?.countdown_timer_text_color) {
        props['_countdown_timer_text_color'] = this.settings.countdown_timer_text_color;
      }
    }

    if (this.settings?.set_ck_items_as_non_returnable) {
      if (isFinalSale === 'true') props['_LPROP'] = 'finalSale';
    }

    props['_added_timestamp'] = new Date().toISOString();

    if (this.strings?.bag_promo_message) {
      props['_bag_promo_message'] = this.strings.bag_promo_message;
    }

    // Add savings banner properties if enabled
    if (this.settings?.enable_add_another_ck_banner) {
      props['_enable_add_another_ck_banner'] = true;
      props['_show_savings_banner'] = this.settings?.show_savings_banner || false;
      props['_discount_type'] = this.settings?.discount_type;
      if (this.settings?.savings_banner_message_override || this.strings?.savings_message) {
        props['_savings_banner_message'] = this.settings?.savings_banner_message_override || this.strings?.savings_message;
      }
      if (this.settings?.ck_banner_message_copy) {
        props['_ck_banner_message_copy'] = this.settings.ck_banner_message_copy;
      }

      if (customKitTotalSavings) {
        props['_custom_kit_total_savings'] = customKitTotalSavings;
      }

      // Add max quantity if available
      if (this.customKitProduct?.max_quantity) {
        props['_max_quantity'] = this.customKitProduct.max_quantity;
      }
    }

    const componentPricingBySlot = await this.getBogoComponentPricingBySlot();
    const bundleComponents = this.getBundleComponents(variantIds, componentPricingBySlot);

    // Build the bundle data structure
    const bundleData = {
      items: [window.buildBundleLineItem({
        parentVariantId: parentId,
        components: bundleComponents,
        properties: props
      })],
      sections: ['cart-drawer', 'cart-icon-bubble']
    };

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bundleData)
      });

      const data = await response.json();

      if (data.status) {
        console.error('Cart error:', data);
        throw new Error(data.description || data.message);
      }

      // Extract line key from added item and update URL
      const addedItem = data.items?.[0];
      if (addedItem?.key) {
        this.ckLineKey = addedItem.key;
        const url = new URL(window.location.href);
        url.searchParams.set('ck_line_key', addedItem.key);
        const customKitUrl = url.toString();
        window.history.replaceState({}, '', customKitUrl);

        // Process ALL items in the bundle (not just the first one)
        data.items.forEach((item, index) => {
          // Copy ck_compare_at_price from properties to top-level for localStorage access
          if (item.properties?.ck_compare_at_price) {
            item.ck_compare_at_price = item.properties.ck_compare_at_price;
            console.log('[CK] Copied ck_compare_at_price to item', index, ':', item.ck_compare_at_price);
          }
        });

        // Append custom_kit_url and added_timestamp to the first item (marker item)
        addedItem.custom_kit_url = customKitUrl;
        addedItem.added_timestamp = new Date().toISOString();
        addedItem.bag_promo_message = this.strings?.bag_promo_message;

        if (this.settings?.enable_bag_countdown_timer) {
          addedItem.countdown_timer_copy = this.strings?.countdown_timer_copy;
          addedItem.countdown_timer_time_amount = this.settings?.countdown_timer_time_amount;
          addedItem.countdown_timer_text_color = this.settings?.countdown_timer_text_color;
        }

        // Add savings banner configuration
        if (this.settings?.enable_add_another_ck_banner) {
          addedItem.enable_add_another_ck_banner = true;
          addedItem.savings_banner_message = this.settings?.savings_banner_message_override || this.strings?.savings_message || '{{amount}} savings';
          addedItem.show_savings_banner = this.settings?.show_savings_banner || false;
          addedItem.discount_type = this.settings?.discount_type;
          addedItem.custom_kit_total_savings = this.totalAmountSaved; // Store total savings in cents
          addedItem.ck_banner_message_copy = this.settings?.ck_banner_message_copy || ''; // Store banner copy text
          addedItem.max_quantity = this.customKitProduct?.max_quantity; // Store max quantity for limit checking

          addedItem.ck_bundle_product_id = this.customKitProduct?.product_id;
          addedItem.ck_original_locale = window?.Shopify?.country || 'US';
        }
      }

      // If editing an existing kit, remove the old item
      if (this.editMode && this.editLineKey && this.editLineKey !== addedItem?.key) {
        await this.removeCartItem(this.editLineKey);
      }

      // Publish cart update event for localStorage sync and other listeners
      // Include the modified addedItem with custom properties
      const cartDataWithCustomProps = { ...data };
      if (addedItem) {
        cartDataWithCustomProps.items = [addedItem];
      }
      this.publishCartUpdate({
        source: 'custom-kit',
        cartData: cartDataWithCustomProps,
      });

      return data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Open custom kit quickshop modal
   * @param {Object} product - The product to show
   * @param {boolean} isEditMode - Whether we're editing an existing selection
   * @param {number} existingVariantId - The variant ID being edited (if in edit mode)
   * @param {number} existingTileIndex - The tile index being edited (if in edit mode)
   */
  async openCustomKitQuickShop(product, isEditMode = false, existingVariantId = null, existingTileIndex = null) {
    if (!this.quickshopStrings || Object.keys(this.quickshopStrings).length === 0) {
      this.loadQuickshopStrings();
    }

    await this.refreshCartVariantQuantities({ forceFetch: true });

    const modal = document.querySelector('#CustomKit-Quickshop-Modal');
    if (!modal) {
      console.error('Custom kit quickshop modal not found');
      return;
    }

    // Store edit context
    this.isEditMode = isEditMode;
    this.editingVariantId = existingVariantId;
    this.editingTileIndex = existingTileIndex;

    // get active collection data
    const activeCollection = this.collections?.[this.currentCollectionIndex] ?? null;
    const hideQuickviewPriceInCollection = Boolean(activeCollection?.hide_collection_prices);

    // Store the current product for later use
    this.currentModalProduct = product;

    // Load product HTML into modal
    modal.classList.add('loading');
    const quickshop = modal.querySelector('#custom-kit-quickshop');

    // Show modal immediately with loading skeleton
    modal.show();

    try {
      // Use prefetched content if available, otherwise fetch fresh
      let responseText;
      if (this.prefetchPromise) {
        responseText = await this.prefetchPromise;
        this.prefetchPromise = null;
      }
      if (!responseText) {
        const response = await fetch(`${product.url}`);
        responseText = await response.text();
      }

      // Parse HTML and extract product-info
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const productInfo = html.querySelector('product-info');

      if (!productInfo) {
        throw new Error('Product info not found in quickshop response');
      }

      productInfo.setAttribute('data-view-context', 'Quickview');
      productInfo.setAttribute('data-update-url', 'false');
      quickshop.innerHTML = '';
      quickshop.appendChild(productInfo);

      // Update swatch IDs to avoid conflicts
      const swatches = quickshop.querySelectorAll('.swatch-input__input');
      swatches.forEach((input, index) => {
        const originalId = input.id;
        const newId = `ck-qv-${originalId || `swatch-${index}`}`;
        input.id = newId;

        const label = quickshop.querySelector(`label[for="${originalId}"]`);
        if (label) {
          label.setAttribute('for', newId);
        }
      });

      // Mark swatches as quickview
      quickshop.querySelectorAll('.swatch-input__input').forEach((swatch) => {
        swatch.setAttribute('data-quickview', 'true');
      });

      this.applyQuickshopVariantInventoryCaps(quickshop, product, { isEditMode, existingTileIndex });

      // show qv promo price
      this.showQuickshopPrice(product, quickshop);

      if (this.settings?.hide_quickview_price || hideQuickviewPriceInCollection) {
        quickshop.classList.add('hide-product-price');
      } else {
        quickshop.classList.remove('hide-product-price');
      }

      // display promo message from ck header
      const progressTitle = this.settings?.progress_header_title;
      if (progressTitle) {
        const productTitle = quickshop.querySelector('.product__title');
        if (productTitle && !productTitle.querySelector('.custom-kit-progress-title')) {
          const subtitle = document.createElement('div');
          subtitle.className = 'custom-kit-progress-title custom-kit-promo-message body-sm';
          subtitle.textContent = progressTitle;
          productTitle.after(subtitle);
        }
      }

      // remove non qv elements
      this.removeNonQuickviewElements(quickshop);
      this.moveQuickshopRatingsInsideWrapper(quickshop);

      const tabs = this.preparePdpTabsForQuickview(html);
      if (tabs) {
        quickshop.appendChild(tabs);
        this.attachDetailsTabClick(tabs);
      }

      this.refreshYotpoWidgets(quickshop);
      this.handleQuickshopReviewsClick(quickshop);

      // Run hydration scripts
      this.hydrateQuickshop(quickshop);

      // Set up modal close and select handlers
      this.setupModalHandlers(modal, product);
      this.setupQuickshopUI(modal, product);
      this.ensureQuickshopVariantSelection(modal);
      this.applyQuickshopVariantInventoryCaps(quickshop, product, { isEditMode, existingTileIndex });

      const detail = { container: quickshop, product };
      document.dispatchEvent(new CustomEvent('customkitQuickviewLoaded', { detail }));

      // Content loaded, remove loading state
      modal.classList.remove('loading');
    } catch (error) {
      console.error('Error loading product quickshop:', error);
      modal.classList.remove('loading');
    }
  }

  /**
   * Hydrate quickshop with scripts and initialization
   * @param {HTMLElement} quickshop - The quickshop container element
   */
  hydrateQuickshop(quickshop) {
    // Initialize PDP swatches carousel if function exists
    if (typeof this.initPDPSwatchesCarousel === 'function') {
      this.initPDPSwatchesCarousel(quickshop);
    } else if (typeof window.initPDPSwatchesCarousel === 'function') {
      window.initPDPSwatchesCarousel(quickshop);
    }

    // Execute gallery script
    const galleryScript = quickshop.querySelector('#galleryScript');
    if (galleryScript) {
      try {
        eval(galleryScript.innerHTML);
      } catch (error) {
        console.error('Error executing gallery script:', error);
      }
    }

    // Execute badge script
    const badgeScript = quickshop.querySelector('#productBadgeScript');
    if (badgeScript) {
      try {
        eval(badgeScript.innerHTML);
      } catch (error) {
        console.error('Error executing badge script:', error);
      }
    }

    // Execute shade modal script
    const shadeScript = quickshop.querySelector('#shadeModalScript');
    if (shadeScript) {
      try {
        eval(shadeScript.innerHTML);
        if (typeof window.initShadeModal === 'function') {
          window.initShadeModal();
        }
      } catch (error) {
        console.error('Error executing shade script:', error);
      }
    }

    // Dispatch product view event if needed
    let detail = {};
    const productJsonScript = quickshop.querySelector('script.product-json-data');
    const selectedProductJsonScript = quickshop.querySelector('script.selected-product-json-data');

    if (productJsonScript && selectedProductJsonScript) {
      try {
        const productData = JSON.parse(productJsonScript.textContent);
        const selectedProductData = JSON.parse(selectedProductJsonScript.textContent);
        detail = { productData, selectedProductData };
      } catch (error) {
        console.error('Error parsing product JSON:', error);
      }
    }

    // Dispatch event for product view
    if (Object.keys(detail).length > 0) {
      const event = new CustomEvent('product-viewed', { detail, bubbles: true });
      document.dispatchEvent(event);
    }
  }

  /**
   * Set up modal event handlers
   * @param {HTMLElement} modal - The modal element
   * @param {Object} product - The product object
   */
  setupModalHandlers(modal, product) {
    // Handle close button
    const closeButton = modal.querySelector('#ModalClose-customkit-quickshop');
    if (closeButton) {
      closeButton.onclick = () => {
        this.detachQuickshopVariantWatcher(modal);
        this.detachJoinWaitlistListener(modal);
        modal.hide();
        this.currentModalProduct = null;
        // Clear multi-product VTO context when quickshop closes
        if (this.settings?.enable_quickview_multi_vto) {
          window.__multiVtoProducts = null;
          window.__multiVtoMeta = null;
        }
      };
    }

    // Handle select button
    const selectButton = modal.querySelector('[data-custom-kit-select]');
    if (selectButton) {
      selectButton.onclick = () => {
        this.handleQuickshopAdd(modal, product);
      };
    }

    const hasVariants = Array.isArray(product?.variants) && product.variants.length > 1;
    this.attachJoinWaitlistListener(modal, product, hasVariants);

    // Handle VTO opener for multi-product experience
    if (this.settings?.enable_quickview_multi_vto === true) {
      this.setMultiProductVtoData(product);
    }
  }

  /**
   * Handle quickshop add/edit from modal
   * @param {HTMLElement} modal - The modal element
   * @param {Object} product - The product object
   */
  async handleQuickshopAdd(modal, product) {
    // Get selected variant from product-info
    const productInfo = modal.querySelector('product-info');
    if (!productInfo) {
      console.error('Product info not found in modal');
      return;
    }

    await this.refreshCartVariantQuantities({ forceFetch: true });

    const checkedVariantInput = productInfo.querySelector('input[type="radio"][data-variant-id]:checked');
    const hiddenVariantInput = productInfo.querySelector('[name="id"]');

    let selectedVariantId = null;
    if (checkedVariantInput?.dataset?.variantId) {
      selectedVariantId = Number(checkedVariantInput.dataset.variantId);
    } else if (hiddenVariantInput?.value) {
      selectedVariantId = Number(hiddenVariantInput.value);
    }

    if (!selectedVariantId) {
      alert('Please select a variant');
      return;
    }

    // Find the variant data before mutating the current kit selection.
    const variant = product.variants.find(v => Number(v.id) === selectedVariantId);
    if (!variant) {
      console.error('Selected variant not found:', selectedVariantId);
      return;
    }

    // If in edit mode, remove the existing item first
    if (this.isEditMode && this.editingVariantId) {
      // Check if selected variant is different from the one being edited
      if (selectedVariantId === this.editingVariantId) {
        // Same variant selected, just close modal
        modal.hide();
        this.currentModalProduct = null;
        this.isEditMode = false;
        this.editingVariantId = null;
        this.editingTileIndex = null;
        return;
      }

      if (!this.canAddVariantWithinInventory(product, variant, { excludedTileIndex: this.editingTileIndex })) {
        this.applyQuickshopVariantInventoryCaps(modal, product, { isEditMode: true, existingTileIndex: this.editingTileIndex });
        this.updateQuickshopCTAAvailability(modal, product, { hasVariants: true, isEditMode: true });
        return;
      }

      // Remove the existing item
      if (this.editingTileIndex != null) {
        this.removeItemByTileIndex(this.editingTileIndex);
      } else {
        this.removeItem(this.editingVariantId, product.of_collection_id);
      }
    } else {
      // Check collection limits
      const { canAdd, message } = this.canAddToCollection(product.of_collection_id);

      if (!canAdd) {
        alert(message);
        modal.hide();
        return;
      }
    }

    if (!this.canAddVariantWithinInventory(product, variant)) {
      this.applyQuickshopVariantInventoryCaps(modal, product, { isEditMode: false });
      this.updateQuickshopCTAAvailability(modal, product, { hasVariants: true, isEditMode: false });
      return;
    }

    // Add item to kit
    this.addItem(product, variant);

    // Close modal and reset edit context
    this.detachQuickshopVariantWatcher(modal);
    this.detachJoinWaitlistListener(modal);
    modal.hide();
    this.currentModalProduct = null;
    this.isEditMode = false;
    this.editingVariantId = null;
    this.editingTileIndex = null;
    this.render();
  }

  /**
   * Handle removal of non quickview elements in quickshop modal
   * @param {HTMLElement} modal - The modal element
   */
  removeNonQuickviewElements(modal) {
    const rootEl = modal.querySelector('product-info') || modal;

    const removeSelectors = [
      'pickup-availability',
      '.product-size-selection',
      '.mobile-product-messaging',
      '.product__upsell-header',
      '.product__upsell',
      '.buy-buttons',
      '[id*="ordergroove_subscriptions_pdp_offer_block"]',
      '.ordergroove-wrapper',
      '.ordergroove'
    ].join(',');

    rootEl.querySelectorAll(removeSelectors).forEach(el => el.remove());
    rootEl.querySelectorAll('.product__info-wrapper [id*="__yotpo_product_reviews_star_rating"]').forEach(el => el.remove());

    const variants =
      rootEl.querySelector('variant-selects') ||
      rootEl.querySelector('variant-radios') ||
      rootEl.querySelector('.product-form__variants');

    if (!variants) return;

    const KEEP_SCRIPT_IDS = new Set(['swatchCarouselScript']);
    const keepSelectors = [
      '.product-form__inventory',
      '.product-form__availability',
      '.product-form__labels',
      '.product-form__oos-message',
      '.product-details-complex-container',
      '.simple-product-details__container'
    ].join(',');

    const shouldKeep = (node) => {
      if (!node) return false;

      if (node.tagName === 'SCRIPT' && KEEP_SCRIPT_IDS.has(node.id)) {
        return true;
      }

      if (node.matches?.(keepSelectors)) {
        return true;
      }

      return false;
    };

    let node = variants.nextElementSibling;
    while (node) {
      const next = node.nextElementSibling;
      if (!shouldKeep(node)) node.remove();
      node = next;
    }
  }

  moveQuickshopRatingsInsideWrapper(scope) {
    const root = scope?.querySelector('product-info') || scope;
    if (!root) return;

    const ratings = root.querySelector('.ratings-wrapper.product-info__ratings-wrapper');
    if (!ratings) return;

    const wrapper = root.querySelector('.product-outer-wrapper');

    if (!wrapper ) return;
    if (wrapper.contains(ratings)) return;

    wrapper.insertAdjacentElement('afterbegin', ratings);
  }

  showQuickshopReviews(root = document.querySelector('#custom-kit-quickshop')) {
    if (!root) return false;

    const reviewsTab = root.querySelector('#ck-qv-tab-reviews');
    if (!reviewsTab) return false;

    reviewsTab.click();
    const tabsContainer =
      reviewsTab.closest('.product-details-complex-container') ||
      root.querySelector('.product-details-complex-container');
    tabsContainer?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    return true;
  }

  handleQuickshopReviewsClick(scope = document.querySelector('#custom-kit-quickshop')) {
    const root = scope;
    if (!root || root.dataset.reviewsClickBound === 'true') return;

    const selector = '.ratings-wrapper, .star-rating-container, .yotpo-reviews-star-ratings-widget';
    const onClick = (event) => {
      const trigger = event.target.closest(selector);
      if (!trigger || !root.contains(trigger)) return;

      event.preventDefault();

      this.showQuickshopReviews(root);
    };

    root.addEventListener('click', onClick);
    root.dataset.reviewsClickBound = 'true';
  }

  async openQuickshopAndShowReviews(product, isEditMode = false, existingVariantId = null, existingTileIndex = null) {
    if (!product) return;

    try {
      await this.openCustomKitQuickShop(product, isEditMode, existingVariantId, existingTileIndex);

      const quickshop = document.querySelector('#custom-kit-quickshop');
      this.handleQuickshopReviewsClick(quickshop);
      this.showQuickshopReviews(quickshop);
    } catch (error) {
      console.error('Error opening quickshop to reviews tab:', error);
    }
  }

  setupQuickshopUI(modal, product) {
    if (this.isEditMode) {
      this.setupQuickshopEditUI(modal, product);
    } else {
      this.setupQuickshopDefaultUI(modal, product);
    }
  }

  setupQuickshopDefaultUI(modal, product) {
    const container = modal.querySelector('.selected-shade-container');
    if (!container) return;

    modal.classList.remove('edit-mode');
    this.updateQuickshopHeader(modal, false);
    this.detachQuickshopVariantWatcher(modal);

    if (!this.defaultQuickshopCTAMarkup) {
      this.defaultQuickshopCTAMarkup = container.innerHTML;
    }

    container.classList.remove('edit-mode');
    container.innerHTML = this.defaultQuickshopCTAMarkup;

    const hasVariants = Array.isArray(product?.variants) && product.variants.length > 1;
    const button = container.querySelector('button');

    if (button) {
      const label = hasVariants
        ? (this.quickshopStrings?.button?.variants || this.strings?.choose || 'Select item')
        : (this.quickshopStrings?.button?.single || this.strings?.add_to_bag || 'Add item');
      const joinLabel = this.quickshopStrings?.button?.join || this.strings?.join_waitlist || 'Join waitlist';
      button.dataset.availableText = label;
      button.dataset.joinText = joinLabel;

      const handledByBagSpecial = this.setupBagSpecialQuickviewCta(modal, product, button, hasVariants);

      if (!handledByBagSpecial) {
        button.onclick = (event) => {
          if (button.classList.contains('join-waitlist')) {
            event.preventDefault();
            this.handleQuickshopJoinWaitlist(modal, product, hasVariants);
            return;
          }
          if (button.disabled) return;
          event.preventDefault();
          this.handleQuickshopAdd(modal, product);
        };
      }

      this.attachQuickshopVariantWatcher(modal, product, { hasVariants, isEditMode: false });
    }

    this.updateSelectedShadeUI(modal, product, { hasVariants, isEditMode: false });
  }

  ensureQuickshopVariantSelection(modal) {
    if (!this.isEditMode || !this.editingVariantId) return;

    const targetVariantId = Number(this.editingVariantId);
    if (Number.isNaN(targetVariantId)) return;

    const variantInput = modal.querySelector(`input[type="radio"][data-variant-id="${targetVariantId}"]`);
    if (variantInput) {
      if (!variantInput.checked) {
        variantInput.checked = true;
      }
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const hiddenInput = modal.querySelector('product-info input[name="id"]');
    if (hiddenInput) {
      hiddenInput.value = targetVariantId;
      hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  setupBagSpecialQuickviewCta(modal, product, button, hasVariants = false) {
    const ctx = window.bagSpecialQuickviewState;
    if (!ctx || !ctx.requireQuickviewCta || ctx.modalSelector !== '#CustomKit-Quickshop-Modal') {
      return false;
    }

    const stateProductId = ctx.productId ? Number(ctx.productId) : null;
    const modalProductId = product?.id ? Number(product.id) : null;
    if (stateProductId && modalProductId && stateProductId !== modalProductId) {
      return false;
    }

    const scope = modal.querySelector('#custom-kit-quickshop') || modal;
    if (!scope || typeof window.addToCartBySku !== 'function') {
      return false;
    }

    const resolveVariant = () => {
      if (typeof window.getBagSpecialVariantFromScope === 'function') {
        return window.getBagSpecialVariantFromScope(scope);
      }

      const selectedRadio = scope.querySelector('input[type="radio"][data-variant-id]:checked');
      if (selectedRadio) {
        return {
          id: Number(selectedRadio.dataset.variantId),
          available: selectedRadio.dataset.variantAvailable !== 'false'
        };
      }

      const hiddenInput = scope.querySelector('product-info input[name="id"]');
      return hiddenInput?.value ? { id: Number(hiddenInput.value), available: true } : null;
    };

    const setProcessing = (status) => {
      button.disabled = status;
      button.setAttribute('aria-disabled', String(status));
    };

    button.dataset.bagSpecialDirectAdd = 'true';

    button.onclick = async (event) => {
      event.preventDefault();

      if (button.classList.contains('join-waitlist')) {
        this.handleQuickshopJoinWaitlist(modal, product, hasVariants);
        return;
      }

      if (button.disabled) return;

      const variant = resolveVariant();
      if (!variant || !variant.id) {
        console.warn('Bag special quickview: variant missing');
        return;
      }

      if (variant.available === false) {
        window.updateBagSpecialQuickviewCtaState?.(variant);
        return;
      }

      const productContainer = ctx.contextEl || ctx.button?.closest('.recommended-product');
      const shouldBlock = await window.preventAddIfMaxedOut?.(variant.id, productContainer);
      if (shouldBlock) return;

      const revertButtonState = window.setBagSpecialButtonAddedState?.(ctx.button);
      try {
        setProcessing(true);
        if (typeof modal.hide === 'function') {
          modal.hide();
        } else {
          modal.removeAttribute('open');
        }

        const bagSpecialSavings = window.getBagSpecialSavings?.(productContainer);

        // Queue the savings to be applied when cart data updates
        if (bagSpecialSavings) {
          window.queueBagSpecialSavings?.(variant.id, bagSpecialSavings);
        }

        await window.addToCartBySku(variant.id, 'recommended', false, bagSpecialSavings);

        if (ctx.button) {
          ctx.button.dataset.variantId = variant.id;
        }
        window.queueBagSpecialRemoval?.(productContainer, modal);
        window.bagSpecialQuickviewState = null;
      } catch (error) {
        console.error('Failed to add bag special from quickview:', error);
        revertButtonState?.();
        setProcessing(false);
      }
    };

    ctx.quickviewScope = scope;
    ctx.quickviewCta = button;
    ctx.processing = false;
    ctx.selectedVariant = resolveVariant();
    window.updateBagSpecialQuickviewCtaState?.(ctx.selectedVariant);

    return true;
  }

  setupQuickshopEditUI(modal, product) {
    const container = modal.querySelector('.selected-shade-container');
    if (!container) return;

    if (!this.defaultQuickshopCTAMarkup) {
      this.defaultQuickshopCTAMarkup = container.innerHTML;
    }

    modal.classList.add('edit-mode');
    this.updateQuickshopHeader(modal, true);
    this.detachQuickshopVariantWatcher(modal);

    container.classList.add('edit-mode');
    container.innerHTML = '';

    const removeLabel =
      this.quickshopStrings?.button?.remove ||
      this.strings?.remove ||
      'Remove item';
    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'cta-button secondary custom-kit-remove';
    removeButton.textContent = removeLabel;
    removeButton.addEventListener('click', (event) => {
      event.preventDefault();
      this.handleQuickshopRemove(modal, product);
    });
    container.appendChild(removeButton);

    const hasVariants = Array.isArray(product?.variants) && product.variants.length > 1;
    if (hasVariants) {
      const saveLabel =
        this.quickshopStrings?.button?.save ||
        container.dataset.saveText ||
        'Save changes';
      container.dataset.saveText = saveLabel;
      const saveButton = document.createElement('button');
      saveButton.type = 'button';
      saveButton.className = 'cta-button primary custom-kit-save';
      saveButton.textContent = saveLabel;
      saveButton.setAttribute('disabled', 'disabled');
      saveButton.setAttribute('aria-disabled', 'true');
      saveButton.addEventListener('click', (event) => {
        if (saveButton.classList.contains('join-waitlist')) {
          event.preventDefault();
          this.handleQuickshopJoinWaitlist(modal, product, hasVariants);
          return;
        }
        if (saveButton.disabled) return;
        event.preventDefault();
        this.handleQuickshopAdd(modal, product);
      });
      container.appendChild(saveButton);
      this.attachQuickshopVariantWatcher(modal, product, { hasVariants, isEditMode: true });
    } else {
      removeButton.classList.add('full-width');
    }
  }

  updateSelectedShadeUI(modal, product, options = {}) {
    if (!modal || options.isEditMode) return;

    const container = modal.querySelector('.selected-shade-container');
    const shadeWrapper = container?.querySelector('.selected-shade');
    const swatchCanvas = shadeWrapper?.querySelector('.swatch-canvas');
    if (!container || !shadeWrapper || !swatchCanvas) return;

    const hasVariants =
      typeof options.hasVariants === 'boolean'
        ? options.hasVariants
        : (Array.isArray(product?.variants) && product.variants.length > 1);

    shadeWrapper.classList.toggle('hidden', !hasVariants);
    if (!hasVariants) {
      swatchCanvas.removeAttribute('style');
      swatchCanvas.classList.remove('unavailable');
      container.classList.remove('oos');
      return;
    }

    const selectedInput = modal.querySelector('input[type="radio"][data-variant-id]:checked');
    if (!selectedInput) {
      swatchCanvas.removeAttribute('style');
      swatchCanvas.classList.remove('unavailable');
      container.classList.remove('oos');
      return;
    }

    const variantAvailable = selectedInput.dataset.variantAvailable !== 'false';
    container.classList.toggle('oos', !variantAvailable);
    swatchCanvas.classList.toggle('unavailable', !variantAvailable);

    const id = selectedInput.id;
    let label = null;
    if (id) {
      const selector = CSS?.escape ? `label[for="${CSS.escape(id)}"]` : `label[for="${id}"]`;
      label = modal.querySelector(selector);
    }
    if (!label) {
      label = selectedInput.closest('label');
    }

    const swatchStyle = label?.querySelector('.swatch')?.getAttribute('style') || '';
    if (swatchStyle) {
      swatchCanvas.setAttribute('style', swatchStyle.replace('--swatch--', '').replace('width=50', 'width=28'));
    } else {
      swatchCanvas.removeAttribute('style');
    }
  }

  attachQuickshopVariantWatcher(modal, product, options = {}) {
    this.detachQuickshopVariantWatcher(modal);

    if (!options.hasVariants) {
      this.updateQuickshopCTAAvailability(modal, product, options);
      return;
    }

    this.variantChangeHandler = (event) => {
      const target = event.target;
      const isVariantChange = event.type === 'change' && target?.matches('input[type="radio"][data-variant-id]');
      const isProductInfoUpdate = event.type === 'product-info:dom-updated';

      if (isVariantChange || isProductInfoUpdate) {
        this.applyQuickshopVariantInventoryCaps(modal, product, options);
        this.updateQuickshopCTAAvailability(modal, product, options);
      }
    };

    modal.addEventListener('change', this.variantChangeHandler);
    modal.addEventListener('product-info:dom-updated', this.variantChangeHandler);
    this.applyQuickshopVariantInventoryCaps(modal, product, options);
    this.updateQuickshopCTAAvailability(modal, product, options);
  }

  detachQuickshopVariantWatcher(modal) {
    if (this.variantChangeHandler) {
      modal.removeEventListener('change', this.variantChangeHandler);
      modal.removeEventListener('product-info:dom-updated', this.variantChangeHandler);
      this.variantChangeHandler = null;
    }
  }

  attachJoinWaitlistListener(modal, product, hasVariants) {
    this.detachJoinWaitlistListener(modal);

    this.joinWaitlistListener = (event) => {
      const btn = event.target.closest('.selected-shade-container .join-waitlist');
      if (!btn) return;

      event.preventDefault();
      event.stopPropagation();
      this.handleQuickshopJoinWaitlist(modal, product, hasVariants);
    };

    modal.addEventListener('click', this.joinWaitlistListener, true);
  }

  detachJoinWaitlistListener(modal) {
    if (this.joinWaitlistListener) {
      modal.removeEventListener('click', this.joinWaitlistListener, true);
      this.joinWaitlistListener = null;
    }
  }

  handleQuickshopJoinWaitlist(modal, product, hasVariants) {
    let variantId = null;

    if (hasVariants) {
      variantId = modal.querySelector('input[type="radio"][data-variant-id]:checked')?.dataset?.variantId || null;
    } else {
      variantId = product?.variants?.[0]?.id || null;
    }

    if (!variantId) {
      alert(this.strings?.choose || 'Please select a variant');
      return;
    }

    this.showWaitlistForVariant(modal, variantId);
  }

  updateQuickshopCTAAvailability(modal, product, options = {}) {
    const container = modal.querySelector('.selected-shade-container');
    if (!container) return;

    const hasVariants = options.hasVariants;
    const isEditMode = options.isEditMode;
    const selectedVariantInput = modal.querySelector('input[type="radio"][data-variant-id]:checked');
    let selectedVariantId = null;
    if (selectedVariantInput?.dataset?.variantId) {
      const parsedVariantId = Number(selectedVariantInput.dataset.variantId);
      selectedVariantId = Number.isNaN(parsedVariantId) ? null : parsedVariantId;
    }
    const editingVariantId = (() => {
      if (this.editingVariantId == null) return null;
      if (typeof this.editingVariantId === 'number') {
        return Number.isNaN(this.editingVariantId) ? null : this.editingVariantId;
      }
      const parsed = Number(this.editingVariantId);
      return Number.isNaN(parsed) ? null : parsed;
    })();
    const hasSelectedVariant = selectedVariantId !== null;
    const isOriginalVariantSelected =
      Boolean(isEditMode && editingVariantId !== null && hasSelectedVariant) &&
      editingVariantId === selectedVariantId;
    const shouldDisableSaveButton = isEditMode && (!hasSelectedVariant || isOriginalVariantSelected);

    const availability = this.isSelectedVariantAvailable(modal, product, hasVariants);

    const isJoinWaitlist = availability === false;
    if (isEditMode) {
      const saveButton = container.querySelector('.custom-kit-save');
      if (!saveButton) return;
      const availableLabel =
        this.quickshopStrings?.button?.save ||
        container.dataset.saveText ||
        'Save changes';
      const joinLabel =
        this.quickshopStrings?.button?.join ||
        this.strings?.join_waitlist ||
        'Join waitlist';
      saveButton.textContent = isJoinWaitlist ? joinLabel : availableLabel;
      saveButton.classList.toggle('join-waitlist', isJoinWaitlist);
      // Enable button if it's join waitlist, otherwise use shouldDisableSaveButton
      if (isJoinWaitlist || !shouldDisableSaveButton) {
        saveButton.removeAttribute('disabled');
        saveButton.setAttribute('aria-disabled', 'false');
      } else {
        saveButton.setAttribute('disabled', 'disabled');
        saveButton.setAttribute('aria-disabled', 'true');
      }
    } else {
      const addButton = container.querySelector('button');
      if (!addButton) return;
      const availableLabel = addButton.dataset.availableText || addButton.textContent;
      const joinLabel =
        this.quickshopStrings?.button?.join ||
        addButton.dataset.joinText ||
        this.strings?.join_waitlist ||
        'Join waitlist';
      addButton.textContent = isJoinWaitlist ? joinLabel : availableLabel;
      addButton.classList.toggle('join-waitlist', isJoinWaitlist);
      addButton.removeAttribute('disabled');
      addButton.setAttribute('aria-disabled', 'false');
    }

    this.updateSelectedShadeUI(modal, product, { hasVariants, isEditMode });
  }

  isSelectedVariantAvailable(modal, product, hasVariants) {
    if (hasVariants) {
      const selected = modal.querySelector('input[type="radio"][data-variant-id]:checked');
      if (!selected) return null;
      return selected.dataset.variantAvailable !== 'false';
    }

    const singleVariant = product?.variants?.[0];
    if (!singleVariant) return true;
    return singleVariant.available !== false;
  }

  handleQuickshopRemove(modal, product) {
    if (this.editingTileIndex != null) {
      this.removeItemByTileIndex(this.editingTileIndex);
    } else if (this.editingVariantId) {
      this.removeItem(this.editingVariantId, product.of_collection_id);
    }

    this.detachQuickshopVariantWatcher(modal);
    modal.hide();
    this.currentModalProduct = null;
    this.isEditMode = false;
    this.editingVariantId = null;
    this.editingTileIndex = null;
    this.render();
  }

  updateQuickshopHeader(modal, isEditMode) {
    const header = modal.querySelector('.sticky-header');
    const title = header?.querySelector('.heading-3');
    if (!header || !title) return;

    const defaultText = this.quickshopStrings?.title?.variants || header.dataset.titleDefault || title.textContent;
    const editText = this.quickshopStrings?.title?.edit || header.dataset.titleEdit || 'Edit Item';

    title.textContent = isEditMode ? editText : defaultText;
  }

  getNearestWaitlist(scope) {
    return (
      scope?.closest?.('#CustomKit-Quickshop-Modal')?.querySelector('.waitlist-modal') ||
      document.getElementById('Waitlist-Modal-product')
    );
  }

  showWaitlistForVariant(scope, variantId) {
    const wl = this.getNearestWaitlist(scope);
    if (!wl || !variantId) return;

    window.initWaitlistModal?.(wl);

    const setVal = (selector, value) => {
      const el = wl.querySelector(selector);
      if (!el) return;
      el.value = value;
      el.setAttribute('value', value);
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const vid = String(variantId);
    setVal('[name="variant_id"]', vid);
    setVal('[name="id"]', vid);
    wl.dataset.variantId = vid;

    requestAnimationFrame(() => wl.show?.());
  }

  preparePdpTabsForQuickview(html) {
    const query = (root, sel) => root?.querySelector(sel) || null;
    const queryAll = (root, sel) => Array.from(root?.querySelectorAll(sel) || []);
    const normalize = (str) => (str || '').trim().toLowerCase();
    const hasKey = (node, key) =>
      normalize(node?.textContent).includes(key) || normalize(node?.getAttribute?.('aria-controls')).includes(key);

    const source = query(html, '.simple-product-details__container, .product-details-complex');
    if (!source) return null;

    const tabsContainer = source.cloneNode(true);
    tabsContainer.classList.add('product-details-complex-container');

    const tabsAndPanelsContainer = query(tabsContainer, '.tab-container') || tabsContainer;

    const tabList =
      query(tabsContainer, '[role="tablist"]') ||
      query(tabsContainer, '.product-details-complex__tabs');
    if (!tabList) return null;

    tabList.classList.add('product-details-complex__tabs');

    const tabs = queryAll(tabList, '[role="tab"], .details-tab, button');

    let ingredientsTab = null;
    let detailsTab = null;
    let benefitsTab = null;

    for (const tab of tabs) {
      if (!ingredientsTab && hasKey(tab, 'ingredient')) {
        ingredientsTab = tab;
        continue;
      }
      if (!detailsTab && hasKey(tab, 'details')) {
        detailsTab = tab;
        continue;
      }
      if (!benefitsTab && hasKey(tab, 'benefits')) {
        benefitsTab = tab;
        continue;
      }
    }

    if (ingredientsTab) {
      const panelId = ingredientsTab.getAttribute('aria-controls');
      ingredientsTab.remove();
      if (panelId) {
        const selector = CSS?.escape ? `#${CSS.escape(panelId)}` : `#${panelId}`;
        query(tabsContainer, selector)?.remove();
      }
    }

    if (detailsTab) detailsTab.textContent = 'details';
    if (benefitsTab) benefitsTab.textContent = 'benefits';

    const yotpoReviewsBlockHtml =
      query(html, '[id*="__yotpo_product_reviews_reviews"]')?.outerHTML ||
      query(html, '.yotpo-widget-instance[data-yotpo-instance-id]')?.outerHTML ||
      '';

    const reviewsTab = document.createElement('button');
    reviewsTab.className = 'details-tab';
    reviewsTab.setAttribute('role', 'tab');
    reviewsTab.id = 'ck-qv-tab-reviews';
    reviewsTab.setAttribute('aria-controls', 'ck-qv-panel-reviews');
    reviewsTab.setAttribute('aria-selected', 'false');
    reviewsTab.textContent = 'reviews';

    if (benefitsTab) {
      benefitsTab.after(reviewsTab);
    } else {
      tabList.appendChild(reviewsTab);
    }

    const reviewsPanel = document.createElement('div');
    reviewsPanel.id = 'ck-qv-panel-reviews';
    reviewsPanel.className = 'product-details-complex__tab-content';
    reviewsPanel.setAttribute('role', 'tabpanel');
    reviewsPanel.hidden = true;
    reviewsPanel.innerHTML =
      `<div id="customkit-quickview-reviews" aria-live="polite">${yotpoReviewsBlockHtml}</div>`;

    tabsAndPanelsContainer.appendChild(reviewsPanel);

    return tabsContainer;
  }

  attachDetailsTabClick(tabsContainer) {
    const tabList = tabsContainer?.querySelector('[role="tablist"]');
    if (!tabList) return;

    tabList.querySelectorAll(':scope > [role="tab"]').forEach(tab => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        const targetTab = event.currentTarget;
        const list = targetTab.closest('.product-details-complex__tabs') || tabList;
        const container = list.closest('.product-details-complex') || tabsContainer;

        list.querySelectorAll('[role="tab"]').forEach(t => t.setAttribute('aria-selected', 'false'));
        targetTab.setAttribute('aria-selected', 'true');

        container.querySelectorAll(':scope > [role="tabpanel"]').forEach(panel => panel.setAttribute('hidden', 'true'));

        const panelId = targetTab.getAttribute('aria-controls');
        if (panelId) {
          const selector = CSS?.escape ? `#${CSS.escape(panelId)}` : `#${panelId}`;
          container.querySelector(selector)?.removeAttribute('hidden');
        }
      });
    });
  }

  initPDPSwatchesCarousel(modal) {
    if (!modal) return;

    const carousels = modal.querySelectorAll('.swatches-carousel.swiper');

    carousels.forEach((carousel) => {
      const slideCount = carousel.querySelectorAll('.swiper-slide').length || 0;

      if (slideCount === 0 || carousel.classList.contains('swiper-initialized')) return;

      if (slideCount === 1) {
        const carouselParent = carousel.parentNode;
        const fieldsetElement = carouselParent?.parentNode;
        const legendElement = fieldsetElement?.querySelector('legend');
        if (legendElement) {
          legendElement.classList.add('single-variant-swatch');
        }
      }

      if (slideCount <= 6) {
        carousel.classList.add('swiper--no-arrows');
      }

      const carouselContainer = carousel.closest('.swatches-carousel-container');
      const swatchesCarouselNext = carouselContainer?.querySelector('.swiper-button-next');
      const swatchesCarouselPrev = carouselContainer?.querySelector('.swiper-button-prev');
      const swatchesCarouselScrollbar = carousel.querySelector('.swiper-scrollbar');

      const swiperInstance = new Swiper(carousel, {
        slidesPerView: 6,
        slidesPerGroup: 6,
        loop: false,
        navigation: {
          nextEl: swatchesCarouselNext,
          prevEl: swatchesCarouselPrev,
        },
        scrollbar: {
          el: swatchesCarouselScrollbar,
          draggable: true,
        },
        breakpoints: {
          1023: {
            allowTouchMove: false,
            slidesPerView: 6,
            slidesPerGroup: 6,
          },
        },
      });

      this.setDefaultShade(carousel);

      const selectedSwatch = carousel.querySelector('.swiper-slide input.shade-input:checked');
      if (selectedSwatch) {
        const selectedSlide = selectedSwatch.closest('.swiper-slide');
        const selectedIndex = Array.from(carousel.querySelectorAll('.swiper-slide')).indexOf(selectedSlide);
        if (selectedIndex >= 0) {
          swiperInstance.slideTo(selectedIndex, 0);
        }
      }
    });
  }

  setDefaultShade(containerEl) {
    if (!containerEl) return;

    const parentSelectsEl = containerEl.closest('.product-variant-picker');
    const defaultId = parentSelectsEl?.dataset.defaultVariantId || containerEl.dataset.defaultVariantId;
    const firstAvailableId = parentSelectsEl?.dataset.firstAvailableVariantId;

    const selectedInput = defaultId
      ? containerEl.querySelector(`.shade-input[data-variant-id="${defaultId}"]`)
      : null;
    const fallbackInput = firstAvailableId
      ? containerEl.querySelector(`.shade-input[data-variant-id="${firstAvailableId}"]`)
      : null;

    const isVariantInStock = selectedInput && selectedInput.dataset.variantAvailable !== 'false';

    if (selectedInput && isVariantInStock) {
      selectedInput.click();
    } else {
      fallbackInput?.click();
    }
  }

  showQuickshopPrice(product, scope) {
    if (!product || !scope) return;

    const priceHtml = this.buildQuickshopPriceHtml(product);
    if (!priceHtml) return;

    scope.querySelectorAll('.product__info-container .price-block').forEach(node => node.remove());

    const titles = scope.querySelectorAll('.product__title');
    titles.forEach(title => {
      title.querySelectorAll('.kit-price').forEach(el => el.remove());
      title.insertAdjacentHTML('beforeend', priceHtml);
    });
  }

  buildQuickshopPriceHtml(product) {
    const isPercentageDiscount = this.isPercentagePriceDisplayMode();
    const originalPrice = isPercentageDiscount ? product.price : (product.compare_at_price || product.price);
    const originalPriceFormatted = isPercentageDiscount ? product.price_formatted : (product.compare_at_price_formatted || product.price_formatted);
    const showOriginalPrice = this.settings?.show_strikethrough_pricing !== false && originalPrice !== product.discount_price;
    let html = '<div class="price__sale kit-price">';
    if (showOriginalPrice) {
      html += `<span class="price-item price-item--regular price-strike">${originalPriceFormatted}</span>`;
    }
    html += `<span class="price-item price-item--last">${product.discount_price_formatted}</span>`;
    html += '</div>';

    return html;
  }

  /**
   * Handle yotpo widget refresh in quickshop modal
   * @param {HTMLElement} modal - The modal element
   */
  refreshYotpoWidgets(modal) {
    const scope = modal;
    if (!scope) return;

    window.yotpoWidgetsContainer?.initWidgets();
  }

  openCart() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.open) {
      cartDrawer.open();
    } else {
      document.querySelector('.mini-bag-button, [data-modal="#Cart-Modal"], [data-cart-trigger]')?.click();
    }
  };
}
if (!customElements.get('custom-kit')) {
  customElements.define('custom-kit', CustomKit);
}
